import i18n from '@/i18n'
import Vue from 'vue'
import store from '@/store'
import { Api } from '@/core/api.js'
import { FORMAT } from '@/constants'
import { buildHierarchicalOrderedItems, getBaseOrder, getItemsBySection as getItemsBySectionFromItems } from '@/store/utils/checklistItems'
import { postTaskWithRetry, shouldQueueOfflineSync } from '@/utils/taskApiRetry'
import { db_issues, db_audits, db_audits_queue, db_templates } from '@/database'

const api = new Api()

// Triquiñuela para resetear el state
const getDefaultState = () => {
    return {
        stats: {
            alerts: {},
            activity: {}
        },
        items: {},
        supervisor: false,
        templates: {},
        headers: {},
        type: 2,
        number_items: 0,
        number_page: 1,
        open_count: 0,
        hasApiError: false,
        hasTemplatesApiError: false,
        config: {
            date_format: 'YYYY-MM-DD',
            hour_format: 'HH:mm',
            datetime_format: 'YYYY-MM-DD HH:mm',
            has_score: true,
            has_state: false,
            has_timing: false,
            has_mandatory: false,
            is_autosave: true,
            can_create: true,
            item_search: false,
            has_calendar: false,
            temporal_view: false,
            close_when_completed: false,
            auth_users: {
                pin: 2,
                review: 2,
                task: 1
            },
            can_edit: {
                title: false,
                state: false,
                inPast: false,
                inTime: true,
                inFuture: true
            },
            item: {
                itemsTitle: [FORMAT.TITLE, FORMAT.SUBTITLE],
                itemsImage: [FORMAT.PHOTO, FORMAT.SIGNATURE],
                itemsInfo: [FORMAT.DOCUMENT, FORMAT.MESSAGE],
                itemsExcluded: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.DOCUMENT, FORMAT.MESSAGE, FORMAT.LINK, FORMAT.RESOURCE],
                itemsVisibles: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.MESSAGE],
                can_pospone: false,
                rate: true, // Esto es por si van a tener puntuaciones en funcion de lo que se responda
                can_review: false,
                has_periodicity: false,
                has_score: true
            }
        },
        actionPlanTemplates: {},
        isLoading: false,
        templatesLoading: false,
        templatesLoaded: false
    }
}

const state = getDefaultState()

const getters = {
    getIsLoading: (state) => {
        return state.isLoading
    },
    getTemplatesLoading: (state) => {
        return state.templatesLoading
    },
    getTemplatesLoaded: (state) => {
        return state.templatesLoaded
    },
    getAll: (state, getters, rootState) => {
        return state.items
    },
    getItemsWithTemporalId(state, getters, rootState) {
        return Object.values(state.items).filter((item) => item.temporal_id)
    },
    getById: (state, getters, rootState) => (id) => {
        return id ? state.items[id] : false
    },
    getConfig: (state, getters, rootState) => {
        return { ...state.config }
    },
    getTemplates: (state, getters, rootState) => {
        return state.templates
    },
    getActionPlanTemplates: (state, getters, rootState) => {
        return state.actionPlanTemplates
    },
    getTemplateById: (state, getters, rootState) => (id) => {
        return state.templates[id] ? state.templates[id] : {}
    },
    getSubtasks: (state, getters, rootState) => (check, task) => {
        let tasks = check ? state.items[check] : {}
        let subtask = []

        if (tasks.sections) {
            Object.keys(tasks.sections).map(function (sec_id, i) {
                var section = tasks.sections[sec_id]

                Object.keys(section.items).map(function (item_id, i) {
                    var item = section.items[item_id]

                    if (item.parent_id == task) {
                        subtask.push(item.item_id)
                    }
                })
            })
        }

        return subtask
    },
    getNumberItems: (state, getters) => {
        return state.number_items
    },
    getNumberPage: (state, getters) => {
        return state.number_page
    },
    getOpenCount: (state, getters) => {
        return state.open_count
    },
    getAllStats: (state, getters, rootState) => {
        return state.stats
    },
    getStats: (state, getters, rootState) => (wich) => {
        return state.stats[wich]
    },
    getSectionFromItem: (state, getters, rootState) => (check, task) => {
        const checklist = state.items[check]
        if (!checklist || !checklist.sections || typeof checklist.sections !== 'object') {
            return false
        }

        let result = false

        Object.values(checklist.sections).forEach(function (section) {
            if (!section || !section.items || typeof section.items !== 'object') {
                return
            }

            Object.values(section.items).forEach(function (item) {
                if (item && item.item_id == task) {
                    result = section.section_id
                }
            })
        })

        return result
    },
    getItems: (state, getters, rootState) => {
        return state.items
    },
    getHeaders: (state, getters, rootState) => {
        return state.headers
    },
    getSupervisor: (state, getters, rootState) => {
        return state.supervisor ? state.supervisor : false
    },
    getItemsBySection: (state) => (check, section) => {
        const items = check && state.items[check] ? state.items[check].items : null
        return getItemsBySectionFromItems(items, section)
    },
    // Action Plan Items of an Audit Item
    getActionPlanItems: (state, getters, rootState) => (auditId, auditItemId, actionPlanId) => {
        const auditSectionId = getters['getSectionFromItem'](auditId, auditItemId)

        const items = state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items

        return items
    },
    getActionPlanPercentage: (state, getters, rootState) => (auditId) => {
        const actionPlanScore = state.items[auditId].data.actionplans_score

        if (actionPlanScore) {
            return parseInt(state.items[auditId].data.actionplans_score)
        } else return 0
    },
    getNotesById: (state) => (id) => {
        if (state.items[id] && state.items[id].notes) {
            return state.items[id].notes
        }
        return state.items[id].notes
    },
    getHasApiError: (state) => {
        return state.hasApiError
    },
    getHasTemplatesApiError: (state) => {
        return state.hasTemplatesApiError
    }
}

const actions = {
    resetState({ commit }) {
        commit('resetState')
    },
    // mantener para auditorías antiguas
    async addIssue(context, params) {
        await context.dispatch('calcScore', params.checklist_ref)
        await context.dispatch('actionPlanCalcScore', params.checklist_ref)

        var oData = { template: params.tpl_id }
        if (params.checklist_ref) {
            oData.checklist_ref = params.checklist_ref

            const checklist = context.getters.getById(params.checklist_ref)
            const total = checklist.total_score
            const answered = checklist.completed
            const score = checklist.score
            const resume = { it: 0, is: 0, a: 0, n: 0 }

            for (let sectionId in checklist.sections) {
                let section = checklist.sections[sectionId]

                for (let itemId in section.items) {
                    let item = section.items[itemId]

                    if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                        if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                        if (item.message) resume.n++ // if item has notes
                        if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                        if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                    }
                }
            }

            oData.total = total
            oData.answered = answered
            oData.score = score
            oData.resume = JSON.stringify(resume)
        }

        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }

        if (params.isOfflineCreated) {
            oData.isOfflineCreated = true
        }

        if (params.toolQueue) {
            oData.toolQueue = params.toolQueue
        }

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/add', oData).then(function (response) {
                var items = { ...state.items }
                var reference = {
                    checklist: Object.values(response.data)[0].id,
                    type: Object.values(response.data)[0].type,
                    status: Object.values(response.data)[0].status
                }

                const checklist = { ...items[params.checklist_ref] }
                for (var s in checklist.sections) {
                    let section = checklist.sections[s]
                    if (typeof section.items[params.item_ref] !== 'undefined') {
                        let item = { ...section.items[params.item_ref] }
                        item.reference = reference
                        items[params.checklist_ref].sections[s].items[params.item_ref] = item
                    }
                }

                Vue.set(state, 'items', items)
                return response.data
            })
        } else {
            if (canWorkOffline) {
                const issue = await store.dispatch('issues/add', oData)
                const reference = {
                    checklist: issue.id,
                    type: issue.type,
                    status: issue.status
                }
                var items = { ...state.items }
                const checklist = { ...items[params.checklist_ref] }

                for (var s in checklist.sections) {
                    const section = checklist.sections[s]
                    if (typeof section.items[params.item_ref] !== 'undefined') {
                        let item = { ...section.items[params.item_ref] }
                        item.reference = reference
                        items[params.checklist_ref].sections[s].items[params.item_ref] = item
                    }
                }

                await db_audits.getItem(params.checklist_ref).then(async (audit) => {
                    if (audit) {
                        for (var s in audit.sections) {
                            const section = audit.sections[s]
                            if (typeof section.items[params.item_ref] !== 'undefined') {
                                let item = { ...section.items[params.item_ref] }
                                item.reference = reference
                                audit.sections[s].items[params.item_ref] = item
                            }
                        }
                        await db_audits.setItem(params.checklist_ref, audit)
                    }
                })

                return issue
            }
        }
    },
    async addActionPlan(context, params) {
        const auditId = params.checklist_ref
        const auditItemId = params.item_ref

        const oData = { template: params.actionPlanId }

        if (!auditId) {
            console.error('addActionPlan is missing data')
            return
        }

        oData.checklist_ref = auditId

        if (auditItemId) {
            oData.item_ref = auditItemId
        }

        if (params.isOfflineCreated) {
            oData.isOfflineCreated = true
        }

        if (params.toolQueue) {
            oData.toolQueue = params.toolQueue
        }

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/add', oData).then((response) => {
                const audits = state.items
                const checklist = audits[auditId]

                // for item.reference
                const reference = {
                    checklist: Object.values(response.data)[0].id,
                    type: Object.values(response.data)[0].type,
                    status: Object.values(response.data)[0].status
                }

                for (var s in checklist.sections) {
                    let section = checklist.sections[s]

                    if (typeof section.items[auditItemId] !== 'undefined') {
                        let item = { ...section.items[auditItemId] }

                        item.reference = reference

                        state.items[auditId].sections[s].items[auditItemId] = item
                    }
                }

                Vue.set(state, 'items', audits)

                context.dispatch('loadById', { id: auditId })

                return response.data
            })
        } else {
            // if (canWorkOffline) {
            //     const issue = await store.dispatch('issues/add', oData)
            //     const reference = {
            //         checklist: issue.id,
            //         type: issue.type,
            //         status: issue.status
            //     }
            //     var items = { ...state.items }
            //     let checklist = { ...items[auditId] }
            //     for (var s in checklist.sections) {
            //         let section = checklist.sections[s]
            //         if (typeof section.items[params.item_ref] !== 'undefined') {
            //             let item = { ...section.items[params.item_ref] }
            //             item.reference = reference
            //             items[auditId].sections[s].items[params.item_ref] = item
            //         }
            //     }
            //     db_audits.getItem(auditId).then((audit) => {
            //         if (audit) {
            //             for (var s in audit.sections) {
            //                 let section = audit.sections[s]
            //                 if (typeof section.items[params.item_ref] !== 'undefined') {
            //                     let item = { ...section.items[params.item_ref] }
            //                     item.reference = reference
            //                     audit.sections[s].items[params.item_ref] = item
            //                 }
            //             }
            //             db_audits.setItem(auditId, audit)
            //         }
            //     })
            //     return issue
            // }
        }
    },
    deleteActionPlan({ commit, state }, params) {
        const oData = {
            checklist: params.action_plan_id,
            employee: params.employeeId,
            audit: params.checklist,
            status: -1
        }

        const item = JSON.parse(JSON.stringify(state.items[params.checklist]))

        if (item && item.sections) {
            Object.values(item.sections).some((section) => {
                if (section.items && section.items[params.item_id]) {
                    delete section.items[params.item_id].actionplans[params.action_plan_id]
                    section.items[params.item_id].reference = false
                    return true
                }
                return false
            })
        }

        commit('setItem', item)

        return api.post('checklist/update', oData).then((response) => {
            return response
        })
    },
    async comment(context, params) {
        const canWorkOffline = context.rootGetters.getOffline

        const checklistType = parseInt(params.cType)
        const auditId = checklistType === 2 ? params.check : params.checkRef
        const auditItemId = checklistType === 2 ? params.answer : params.itemRef

        // API params obj
        let sended

        // only for Audits Items (not Action Plan items)
        if (checklistType === 2) {
            context.dispatch('calcScore', auditId)

            const checklist = context.getters.getById(auditId)
            const total = checklist.total_score
            const answered = checklist.completed
            const score = checklist.score

            const resume = { it: 0, is: 0, a: 0, n: 1 }

            // update resume
            for (let sectionId in checklist.sections) {
                let section = checklist.sections[sectionId]

                for (let itemId in section.items) {
                    let item = section.items[itemId]

                    if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                        if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                        if (item.message) resume.n++ // if item has notes
                        if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                        if (item.alerts && item.alerts.issues.length === 0) resume.a++ // if item has alerts and does not have issue
                    }
                }
            }

            // update API params for Audit item
            sended = {
                action: 'comment',
                checklist: auditId,
                task: auditItemId,
                message: params.message,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume),
                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
            }
        }

        // only for Action Plan Items (checklist type 5)
        if (checklistType === 5) {
            // update API params for Action Plan item
            const actionPlanId = params.check
            const actionPlanItemId = params.answer
            context.dispatch('actionPlanCalcScore', actionPlanId)

            sended = {
                action: 'comment',
                checklist: actionPlanId,
                task: actionPlanItemId,
                message: params.message
                // requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
            }
        }

        if (typeof params.file !== 'undefined') sended.file = params.file

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/comment', sended)
            const checklistId = params.check
            const itemId = params.answer

            if (!response.status && context.rootGetters.getOffline) {
                return context.dispatch('syncAction', sended)
            }

            if (!response.status) {
                let error = {
                    check: checklistId,
                    answer: itemId,
                    getters: context.getters
                }
                if (typeof params.file !== 'undefined') error.image = null
                else error.message = ''

                context.commit('setComment', error)
                return { status: false }
            }

            let returned = {
                check: checklistId,
                answer: itemId,
                message: response.data.message,
                getters: context.getters
            }
            if (typeof response.data.image !== 'undefined') {
                returned.image = response.data.image
            }
            return context.commit('setComment', returned)
        } else if (context.rootGetters.getOffline) {
            let offlineMessage = {
                check: params.check,
                answer: params.answer,
                message: params.message,
                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline,
                getters: context.getters
            }
            if (typeof params.file !== 'undefined') offlineMessage.image = params.file
            return context
                .dispatch('syncAction', sended)
                .then((response) => {
                    return context.commit('setComment', offlineMessage)
                })
                .catch(() => {
                    return context.commit('setComment', offlineMessage)
                })
        } else {
            var error = {
                check: params.check,
                answer: params.answer,
                getters: context.getters
            }
            if (typeof params.file !== 'undefined') error.image = null
            else error.message = ''

            context.commit('setComment', error)
            return { status: false }
        }
    },
    loadItemsPagination(context, params) {
        params.type = state.type
        if (!fnCheckConnection()) {
            return loadItemsOffline(context.commit, context.dispatch)
        }
        return api
            .post('checklist/resume/' + params.id, params)
            .then(async function (response) {
                if (!response || !response.data || !response.data.values) {
                    return loadItemsOffline(context.commit, context.dispatch)
                }
                context.dispatch('format', { data: response.data.values })
                await context.dispatch('calcScore')
                await context.dispatch('actionPlanCalcScore')
                context.commit('setResume', { data: response.data.schema })
                context.commit('setNumberItems', response.data.number_items)
                context.commit('setNumberPage', params.number_pages)
                if (response.data.open_count !== undefined) {
                    context.commit('setOpenCount', response.data.open_count)
                }
                context.commit('setHasApiError', false)
            })
            .catch((error) => {
                console.error(error)
                context.commit('setHasApiError', true)
                return loadItemsOffline(context.commit, context.dispatch)
            })
    },
    defineFinalScore(context, params) {
        const { finalScores, auditScore } = params

        if (!finalScores) {
            return null
        }

        for (let i = 0; i < finalScores.length; i++) {
            const finalScoreData = {
                name: finalScores[i].n,
                color: finalScores[i].c
            }
            const finalScoreOperator = finalScores[i].p.cond
            const finalScoreValue = finalScores[i].p.v

            if (finalScoreOperator === '<>' && auditScore >= finalScoreValue[0] && auditScore <= finalScoreValue[1]) {
                return finalScoreData
            }

            if (finalScoreOperator === '=' && auditScore === parseInt(finalScoreValue)) {
                return finalScoreData
            }

            if (finalScoreOperator === '>' && auditScore > parseInt(finalScoreValue)) {
                return finalScoreData
            }

            if (finalScoreOperator === '>=' && auditScore >= parseInt(finalScoreValue)) {
                return finalScoreData
            }

            if (finalScoreOperator === '<' && auditScore < parseInt(finalScoreValue)) {
                return finalScoreData
            }

            if (finalScoreOperator === '<=' && auditScore <= parseInt(finalScoreValue)) {
                return finalScoreData
            }
        }
    },
    // COMPLETE ACTION
    async complete(context, params) {
        await context.dispatch('calcScore', params.check)
        await context.dispatch('actionPlanCalcScore', params.check)

        var supervisor = context.getters['getSupervisor']
        supervisor = supervisor || params.emp

        // AUDIT
        const checklist = context.getters.getById(params.check)

        const defineFinalScoreParams = {
            auditScore: checklist.score,
            finalScores: params.finalScores
        }

        const finalAuditScore = await context.dispatch('defineFinalScore', defineFinalScoreParams)

        let resume = {
            it: 0,
            is: 0,
            a: 0,
            n: 0,
            ev: finalAuditScore ? { n: finalAuditScore.name, c: finalAuditScore.color } : null
        }

        for (let sectionId in checklist.sections) {
            let section = checklist.sections[sectionId]

            for (let itemId in section.items) {
                let item = section.items[itemId]

                if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                    if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                }
            }
        }
        const canWorkOffline = context.rootGetters.getOffline
        const paramsApi = {
            checklist: params.check,
            employee: supervisor,
            complete_date: params.date,
            resume: JSON.stringify(resume),
            values_not_visible: checklist.values_no_visible
            // type: context.state.type,
        }
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/complete', paramsApi).then(async function (response) {
                if (!response.status) {
                    return response
                }

                const shouldUpdateRadarKPI = !checklist.complete_date
                checklist.complete_date = moment()

                Object.keys(state.templates).map(function (tpl_id, i) {
                    var tpl = state.templates[tpl_id]

                    if (tpl.id == checklist.template_id) {
                        tpl.last_audit = checklist
                        tpl.last_update = moment()
                        tpl.num_progress--
                    }
                })

                // TODO
                params.values = JSON.parse(params.values)
                Object.keys(params.values).map(function (id, i) {
                    var val = params.values[id]
                    context.commit('setAnswer', {
                        check: params.check,
                        answer: val.item_id,
                        checkRef: params.checkRef,
                        itemRef: params.itemRef,
                        emp: supervisor,
                        date: params.date,
                        value: val.value,
                        getters: context.getters
                    })
                })

                await context.dispatch('calcScore', params.check)
                await context.dispatch('actionPlanCalcScore', params.check)
                if (shouldUpdateRadarKPI) {
                    await context.dispatch(
                        'actionplan/updateOpenActionPlansRadarKPIForAudit',
                        {
                            audit: context.getters.getById(params.check) || checklist,
                            delta: 1
                        },
                        { root: true }
                    )
                }
                return response
            })
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'complete'
                const finishOfflineCompletion = async () => {
                    const shouldUpdateRadarKPI = !checklist.complete_date
                    checklist.complete_date = moment()
                    Object.keys(state.templates).map(function (tplId, i) {
                        var tpl = state.templates[tplId]
                        if (tpl.id == checklist.template_id) {
                            tpl.last_audit = checklist
                            tpl.last_update = moment()
                            tpl.num_progress--
                        }
                    })

                    // TODO
                    params.values = JSON.parse(params.values)
                    Object.keys(params.values).map(function (id, i) {
                        var val = params.values[id]
                        context.commit('setAnswer', {
                            check: params.check,
                            answer: val.item_id,
                            checkRef: params.checkRef,
                            itemRef: params.itemRef,
                            emp: supervisor,
                            date: params.date,
                            value: val.value,
                            getters: context.getters
                        })
                    })

                    await context.dispatch('calcScore', params.check)
                    await context.dispatch('actionPlanCalcScore', params.check)
                    if (shouldUpdateRadarKPI) {
                        await context.dispatch(
                            'actionplan/updateOpenActionPlansRadarKPIForAudit',
                            {
                                audit: context.getters.getById(params.check) || checklist,
                                delta: 1
                            },
                            { root: true }
                        )
                    }

                    checklist.resume = resume

                    await db_audits.setItem(params.check, JSON.parse(JSON.stringify(checklist)))
                    const templates = JSON.parse(JSON.stringify(state.templates))
                    await db_templates.setItem(checklist.template_id, JSON.stringify(templates[checklist.template_id]))

                    return { status: true, msg: 'Audit completed' }
                }

                return context.dispatch('syncAction', paramsApi).then(finishOfflineCompletion, finishOfflineCompletion)
            }
        }
    },
    async loadById(context, params) {
        if (!fnCheckConnection()) {
            return loadDetailOffline(context, params.id)
        }

        if (params.noRefresh && fnCheckConnection() && context.rootGetters.getFastConnection) {
            const item = context.getters.getById(params.id)
            context.dispatch('format', { data: item })
            await context.dispatch('calcScore', params.id)
            await context.dispatch('actionPlanCalcScore', params.id)
            return
        }

        context.commit('setIsLoading', true)

        return api
            .get('checklist/id/' + params.id, {})
            .then(async function (response) {
                context.commit('setIsLoading', false)

                return context.dispatch('mergeOffline', response).then(async function (response2) {
                    return context.dispatch('format', { data: response2.data }).then(async function (response3) {
                        await context.dispatch('calcScore', params.id)
                        await context.dispatch('actionPlanCalcScore', params.id)
                    })
                })
            })
            .catch((error) => {
                console.error('Error in loadById():', error)
                context.commit('setIsLoading', false)
            })
    },
    loadTemplates(context, params) {
        context.commit('setTemplatesLoading', true)

        const finishLoading = (result) => {
            context.commit('setTemplatesLoading', false)
            context.commit('setTemplatesLoaded', true)
            return result
        }

        const finishLoadingWithError = (error) => {
            context.commit('setTemplatesLoading', false)
            context.commit('setTemplatesLoaded', true)
            throw error
        }

        if (!fnCheckConnection()) {
            return loadTemplatesOffline(context).then(finishLoading, finishLoadingWithError)
        }

        return api
            .get('checklist/templates/' + state.type, {})
            .then(async function (response) {
                context.commit('setTemplates', { data: response.data })

                _.forEach(_.values(response.data), async function (value, key) {
                    await db_templates.setItem(value.id, JSON.stringify(value))
                })

                var lastAudits = {}

                Object.keys(response.data).map(function (tpl_id, i) {
                    var tpl = { ...response.data[tpl_id] }
                    if (tpl.last_audit) {
                        lastAudits[tpl.last_audit.id] = tpl.last_audit
                    }
                })

                await context.dispatch('format', { data: lastAudits })
                await context.dispatch('calcScore')
                await context.dispatch('actionPlanCalcScore')
                context.commit('setHasTemplatesApiError', false)
            })
            .catch((error) => {
                logError(error)
                context.commit('setHasTemplatesApiError', true)
                return loadTemplatesOffline(context)
            })
            .then(finishLoading, finishLoadingWithError)
    },
    loadActionPlanTemplates(context, params) {
        return api
            .get('checklist/templates/' + 5, {})
            .then((response) => {
                context.commit('setActionPlanTemplates', { data: response.data })
                context.commit('actionplan/setTemplates', { data: response.data }, { root: true })
            })
            .catch((error) => {
                console.error(error)
            })
            .finally(() => {
                // Se marca como cargado siempre (incluso sin plantillas o con error): distingue
                // "sin plantillas creadas" de "aún cargando" para Radar.
                context.commit('actionplan/setTemplatesLoaded', true, { root: true })
            })
    },
    async reload(context, params) {
        context.dispatch('format', { data: checklist })
        await context.dispatch('calcScore')
        await context.dispatch('actionPlanCalcScore')
    },
    async answer(context, params) {
        let value = params.value

        const checklistType = parseInt(params.cType)

        if (checklistType === 5) {
            context.dispatch('actionPlanCalcScore', params.check)
        }

        let supervisor = context.getters['getSupervisor']
        supervisor = supervisor || params.emp

        const canWorkOffline = context.rootGetters.getOffline

        // In case of image or signature type, JSON-parse the value
        if (state.config.item.itemsImage.includes(params.answer_type)) {
            value = JSON.stringify(value)
        }

        context.commit('setAnswer', {
            cType: params.cType,
            check: params.check, // if is audit item, it's the audit template id, if is action plan item, it's the action plan id
            answer: params.answer,
            checkRef: params.checkRef,
            itemRef: params.itemRef,
            emp: supervisor,
            date: params.date,
            value: params.value,
            getters: context.getters,
            bReadingFromSensor: params.bReadingFromSensor,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
        })

        // only when answered item was an Action Plan item
        if (checklistType === 5) {
            context.commit('updateActionPlanPercentage', {
                cType: params.cType,
                check: params.check,
                answer: params.answer,
                checkRef: params.checkRef,
                itemRef: params.itemRef
            })
        }

        const checklist = context.getters.getById(params.check)

        let answered
        let total
        let score
        let resume = { it: 0, is: 0, a: 0, n: 0 }

        // only for Audit item (not Action Plan item)
        if (checklistType === 2) {
            // Commented out because it's not needed
            // const subtask = context.getters.getSubtasks(params.check, params.answer)
            // if (subtask.length > 0) {
            //     Object.keys(subtask).map((itemID, i) => {
            //         const item = subtask[itemID]

            //         context.commit('setReset', {
            //             check: params.check,
            //             answer: item,
            //             getters: context.getters,
            //             cType: params.cType,
            //             checkRef: params.checkRef,
            //             itemRef: params.itemRef
            //         })
            //     })
            // }

            await context.dispatch('calcScore', params.check)

            answered = checklist.completed
            total = checklist.total_score
            score = checklist.score

            for (let sectionID in checklist.sections) {
                let section = checklist.sections[sectionID]

                for (let itemId in section.items) {
                    let item = section.items[itemId]

                    if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                        if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                        if (item.message) resume.n++ // if item has notes
                        if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                        if (item.alerts && item.alerts.issues.length === 0) resume.a++ // if item has alerts and does not have issue
                    }
                }
            }
        }

        // API PARAMS
        let paramsApi

        // API params for AUDIT item
        if (checklistType === 2) {
            paramsApi = {
                checklist: params.check, // Audit template id
                task: params.answer, // Item id
                employee: supervisor,
                complete_date: params.date,
                value: value,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume),
                employee_ref: supervisor,
                type: params.answer_type,
                bReadingFromSensor: params.bReadingFromSensor
            }
        } else {
            // API params for ACTION PLAN item
            paramsApi = {
                checklist: params.check, // Audit template id
                task: params.answer, // Item Id
                employee: supervisor,
                complete_date: params.date,
                value: value,
                audit: params.audit,
                employee_ref: supervisor,
                type: params.answer_type,
                bReadingFromSensor: params.bReadingFromSensor
            }
        }

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'answer' })
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/answer', paramsApi)

            if (!response.status) {
                if (canWorkOffline && shouldQueueOfflineSync(response)) {
                    paramsApi.action = 'answer'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        logError(error)
                    }
                    if (checklistType === 2) {
                        return context.dispatch('calcScore', params.check).then(function () {
                            return { status: false }
                        })
                    }

                    if (checklistType === 5) {
                        return context.dispatch('actionPlanCalcScore', params.audit).then(function () {
                            return { status: false }
                        })
                    }

                    return { status: false }
                }

                context.commit('setReset', {
                    check: params.check,
                    answer: params.answer,
                    getters: context.getters,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef,
                    cType: params.cType
                })

                if (checklistType === 5) {
                    context.commit('updateActionPlanPercentage', {
                        cType: params.cType,
                        check: params.check,
                        answer: params.answer,
                        checkRef: params.checkRef,
                        itemRef: params.itemRef
                    })
                    context.dispatch('actionPlanCalcScore', params.audit)
                }

                const finishFailure = function () {
                    if (response.msg && response.msg === 'Error: Api Error') {
                        return { status: false, msg: 'Error: Api Error' }
                    }
                    return { status: false }
                }

                if (checklistType === 2) {
                    return context.dispatch('calcScore', params.check).then(finishFailure)
                }

                return finishFailure()
            }

            let value = response.data.value
            if (state.config.item.itemsImage.includes(params.answer_type)) {
                value = splitCommasBase64(value)
                context.commit('setAnswer', {
                    cType: params.cType,
                    check: params.check,
                    answer: params.answer,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef,
                    emp: supervisor,
                    date: params.date,
                    value: value,
                    getters: context.getters,
                    bReadingFromSensor: params.bReadingFromSensor
                })
            }

            if (checklistType === 5) {
                context.commit('updateActionPlanPercentage', {
                    cType: params.cType,
                    check: params.check,
                    answer: params.answer,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef
                })
                context.dispatch('actionPlanCalcScore', params.audit)
            }

            return response
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'answer'
                paramsApi.value = JSON.stringify(params.value)

                const finishAfterSync = function () {
                    if (checklistType === 2) {
                        return context.dispatch('calcScore', params.check).then(function () {
                            return { status: false }
                        })
                    }
                    if (checklistType === 5) {
                        return context.dispatch('actionPlanCalcScore', params.audit).then(function () {
                            return { status: false }
                        })
                    }
                    return { status: false }
                }

                return context
                    .dispatch('syncAction', paramsApi)
                    .then(async () => {
                        await db_audits.setItem(params.check, JSON.parse(JSON.stringify(checklist)))
                        return finishAfterSync()
                    })
                    .catch(async () => {
                        await db_audits.setItem(params.check, JSON.parse(JSON.stringify(checklist)))
                        return finishAfterSync()
                    })
            } else {
                context.commit('setReset', {
                    check: params.check,
                    answer: params.answer,
                    getters: context.getters,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef,
                    cType: params.cType
                })

                // only when answered item was an Action Plan item
                if (checklistType === 5) {
                    context.commit('updateActionPlanPercentage', {
                        cType: params.cType,
                        check: params.check,
                        answer: params.answer,
                        checkRef: params.checkRef,
                        itemRef: params.itemRef
                    })
                }

                // only for Audit
                if (checklistType === 2) {
                    return context.dispatch('calcScore', params.check).then(function () {
                        return { status: false }
                    })
                }
            }
        }
    },
    async apply(context, params) {
        const checklistType = parseInt(params.cType)

        const checklist = context.getters.getById(params.check)

        let paramsApi = {} // Inicializar para evitar undefined

        let supervisor = context.getters['getSupervisor']
        supervisor = supervisor || params.emp

        // only for Audit item (not Action Plan item)
        if (checklistType === 2) {
            try {
                if (params.answer) {
                    let subtask = context.getters.getSubtasks(params.check, params.answer)
                    const items = context.getters.getItemsBySection(params.check, params.answer)

                    try {
                        context.commit('setApply', {
                            check: params.check,
                            answer: params.answer,
                            emp: params.emp,
                            date: params.date ? params.date : false,
                            apply: params.apply,
                            getters: context.getters,
                            cType: params.cType,
                            checkRef: params.checkRef,
                            itemRef: params.itemRef
                        })
                    } catch (setApplyError) {
                        console.error('Error en setApply (principal):', setApplyError)
                    }

                    if (checklist.sections && checklist.sections[params.answer]) {
                        Object.keys(items).map((itemId, i) => {
                            let it = items[itemId]
                            if (!it.mandatory) {
                                context.commit('setApply', {
                                    check: params.check,
                                    answer: it.item_id,
                                    sectionId: params.sectionId,
                                    date: params.date ? params.date : false,
                                    apply: params.apply,
                                    getters: context.getters,
                                    cType: params.cType,
                                    checkRef: params.checkRef,
                                    itemRef: params.itemRef
                                })
                            }
                        })
                    }

                    if (subtask.length > 0) {
                        Object.keys(subtask).map(function (itemId, i) {
                            let item = subtask[itemId]

                            context.commit('setReset', {
                                check: params.check,
                                answer: item,
                                getters: context.getters,
                                cType: params.cType,
                                checkRef: params.checkRef,
                                itemRef: params.itemRef
                            })

                            // only when answered item was an Action Plan item
                            if (checklistType === 5) {
                                context.commit('updateActionPlanPercentage', {
                                    cType: params.cType,
                                    check: params.check,
                                    answer: params.answer,
                                    checkRef: params.checkRef,
                                    itemRef: params.itemRef
                                })
                            }
                        })
                    }

                    await context.dispatch('calcScore', params.check)

                    // let checklist = context.getters.getById(params.check)
                } else {
                    context.commit('setApply', {
                        check: params.check,
                        answer: null,
                        sectionId: params.sectionId,
                        emp: params.emp,
                        date: params.date ? params.date : false,
                        apply: params.apply,
                        getters: context.getters,
                        cType: params.cType,
                        checkRef: params.checkRef,
                        itemRef: params.itemRef
                    })
                }

                let total = checklist.total_score
                let answered = checklist.completed
                let score = checklist.score
                let resume = { it: 0, is: 0, a: 0, n: 0 }

                for (let sectionId in checklist.sections) {
                    let section = checklist.sections[sectionId]

                    for (let itemId in section.items) {
                        let item = section.items[itemId]

                        if (item.is_visible) {
                            if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                            if (item.message) resume.n++ // if item has notes
                            if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                            if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                        }
                    }
                }

                paramsApi = {
                    checklist: params.check,
                    task: params.answer || params.sectionId, // Usar sectionId cuando answer es null
                    employee: supervisor,
                    complete_date: params.date,
                    apply: params.apply,
                    total: total,
                    answered: answered,
                    score: score,
                    resume: JSON.stringify(resume)
                }
            } catch (error) {
                console.error('ERROR en bloque checklistType === 2:', error)
                console.error('Error stack:', error.stack)
            }
        }

        // only for Action Plan Item
        if (checklistType === 5) {
            context.commit('setApply', {
                check: params.check,
                answer: params.answer,
                emp: params.emp,
                date: params.date ? params.date : false,
                apply: params.apply,
                getters: context.getters,
                cType: params.cType,
                checkRef: params.checkRef,
                itemRef: params.itemRef
            })

            paramsApi = {
                checklist: params.check,
                task: params.answer,
                employee: supervisor,
                complete_date: params.date,
                apply: params.apply
            }

            context.commit('updateActionPlanPercentage', {
                cType: params.cType,
                check: params.check,
                answer: params.answer,
                checkRef: params.checkRef,
                itemRef: params.itemRef
            })
        }

        // Asegurar que paramsApi tenga valores mínimos
        if (!paramsApi || Object.keys(paramsApi).length === 0) {
            paramsApi = {
                checklist: params.check,
                task: params.answer,
                employee: supervisor,
                complete_date: params.date,
                apply: params.apply
            }
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            try {
                return api
                    .post('checklist/task/apply', paramsApi)
                    .then((response) => {
                        if (!response.status) {
                            if (context.rootGetters.getOffline) {
                                paramsApi.action = 'apply'
                                return context.dispatch('syncAction', paramsApi)
                            } else {
                                context.commit('setReset', {
                                    check: params.check,
                                    answer: params.answer,
                                    getters: context.getters,
                                    cType: params.cType,
                                    checkRef: params.checkRef,
                                    itemRef: params.itemRef
                                })

                                // only when answered item was an Action Plan item
                                if (checklistType === 5) {
                                    context.commit('updateActionPlanPercentage', {
                                        cType: params.cType,
                                        check: params.check,
                                        answer: params.answer,
                                        checkRef: params.checkRef,
                                        itemRef: params.itemRef
                                    })
                                }
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error)

                        if (context.rootGetters.getOffline) {
                            paramsApi.action = 'apply'
                            return context.dispatch('syncAction', paramsApi)
                        } else {
                            context.commit('setReset', {
                                check: params.check,
                                answer: params.answer,
                                getters: context.getters,
                                cType: params.cType,
                                checkRef: params.checkRef,
                                itemRef: params.itemRef
                            })

                            // only when answered item was an Action Plan item
                            if (checklistType === 5) {
                                context.commit('updateActionPlanPercentage', {
                                    cType: params.cType,
                                    check: params.check,
                                    answer: params.answer,
                                    checkRef: params.checkRef,
                                    itemRef: params.itemRef
                                })
                            }
                        }
                    })
            } catch (error) {
                console.error('Error al llamar al endpoint apply:', error)
            }
        } else {
            if (context.rootGetters.getOffline) {
                paramsApi.action = 'apply'
                return context.dispatch('syncAction', paramsApi)
            } else {
                context.commit('setReset', {
                    check: params.check,
                    answer: params.answer,
                    getters: context.getters,
                    cType: params.cType,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef
                })

                // only when answered item was an Action Plan item
                if (checklistType === 5) {
                    context.commit('updateActionPlanPercentage', {
                        cType: params.cType,
                        check: params.check,
                        answer: params.answer,
                        checkRef: params.checkRef,
                        itemRef: params.itemRef
                    })
                }
            }
        }
    },
    async applyrecurrent(context, params) {
        const checklistType = parseInt(params.cType)

        let supervisor = context.getters['getSupervisor']
        supervisor = supervisor || params.emp

        let applyRecurrentAPIParams = {} // Inicializar para evitar undefined

        // only for Audit item (not Action Plan item)
        if (checklistType === 2) {
            try {
                if (params.answer) {
                    let subtask = context.getters.getSubtasks(params.check, params.answer)

                    try {
                        context.commit('setApply', {
                            check: params.check,
                            answer: params.answer,
                            emp: params.emp,
                            date: params.date ? params.date : false,
                            apply: params.apply,
                            getters: context.getters,
                            cType: params.cType,
                            checkRef: params.checkRef,
                            itemRef: params.itemRef
                        })
                    } catch (setApplyError) {
                        console.error('Error en setApply (applyrecurrent):', setApplyError)
                    }

                    let checklist = context.getters.getById(params.check)

                    if (checklist.sections && checklist.sections[params.answer]) {
                        const items = context.getters.getItemsBySection(params.check, params.answer)

                        Object.keys(items).map((itemId, i) => {
                            const it = items[itemId]
                            if (!it.mandatory) {
                                context.commit('setApply', {
                                    check: params.check,
                                    answer: it.item_id,
                                    sectionId: params.sectionId ? params.sectionId : null,
                                    date: params.date ? params.date : false,
                                    apply: params.apply,
                                    getters: context.getters,
                                    cType: params.cType,
                                    checkRef: params.checkRef,
                                    itemRef: params.itemRef
                                })
                            }
                        })
                    }

                    await context.dispatch('calcScore', params.check)

                    if (subtask.length > 0) {
                        Object.keys(subtask).map((item_id, i) => {
                            var item = subtask[item_id]
                            context.commit('setReset', {
                                check: params.check,
                                answer: item,
                                getters: context.getters,
                                cType: params.cType,
                                checkRef: params.checkRef,
                                itemRef: params.itemRef
                            })

                            // only when answered item was an Action Plan item
                            if (checklistType === 5) {
                                context.commit('updateActionPlanPercentage', {
                                    cType: params.cType,
                                    check: params.check,
                                    answer: params.answer,
                                    checkRef: params.checkRef,
                                    itemRef: params.itemRef
                                })
                            }
                        })
                    }
                } else {
                    try {
                        context.commit('setApply', {
                            check: params.check,
                            answer: null,
                            sectionId: params.sectionId,
                            emp: params.emp,
                            date: params.date ? params.date : false,
                            apply: params.apply,
                            getters: context.getters,
                            cType: params.cType,
                            checkRef: params.checkRef,
                            itemRef: params.itemRef
                        })
                    } catch (setApplyError) {
                        console.error('Error en setApply (applyrecurrent - section):', setApplyError)
                    }
                }

                let checklist = context.getters.getById(params.check)
                let total = checklist.total_score
                let answered = checklist.completed
                let score = checklist.score
                let resume = { it: 0, is: 0, a: 0, n: 0 }

                for (let section_id in checklist.sections) {
                    let section = checklist.sections[section_id]

                    for (let item_id in section.items) {
                        let item = section.items[item_id]

                        if (item.is_visible) {
                            if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                            if (item.message) resume.n++ // if item has notes
                            if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                            if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                        }
                    }
                }

                applyRecurrentAPIParams = {
                    checklist: params.check,
                    task: params.answer || params.sectionId, // Usar sectionId cuando answer es null
                    employee: supervisor,
                    complete_date: params.date,
                    apply: params.apply,
                    total: total,
                    answered: answered,
                    score: score,
                    resume: JSON.stringify(resume)
                }
            } catch (error) {
                console.error('ERROR en bloque checklistType === 2 (applyrecurrent):', error)
                console.error('Error stack:', error.stack)
            }
        }

        // only for Action Plan item
        if (checklistType === 5) {
            context.commit('setApply', {
                check: params.check,
                answer: params.answer,
                emp: params.emp,
                date: params.date ? params.date : false,
                apply: params.apply,
                getters: context.getters,
                cType: params.cType,
                checkRef: params.checkRef,
                itemRef: params.itemRef
            })

            applyRecurrentAPIParams = {
                checklist: params.check,
                task: params.answer,
                employee: supervisor,
                complete_date: params.date,
                apply: params.apply
            }
        }

        // Asegurar que applyRecurrentAPIParams tenga valores mínimos
        if (!applyRecurrentAPIParams || Object.keys(applyRecurrentAPIParams).length === 0) {
            applyRecurrentAPIParams = {
                checklist: params.check,
                task: params.answer || params.sectionId,
                employee: supervisor,
                complete_date: params.date,
                apply: params.apply
            }
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            try {
                return api.post('checklist/task/applyrecurrent', applyRecurrentAPIParams).then((response) => {
                    if (!response.status) {
                        context.commit('setReset', {
                            check: params.check,
                            answer: params.answer,
                            getters: context.getters,
                            cType: params.cType,
                            checkRef: params.checkRef,
                            itemRef: params.itemRef
                        })

                        // only when answered item was an Action Plan item
                        if (checklistType === 5) {
                            context.commit('updateActionPlanPercentage', {
                                cType: params.cType,
                                check: params.check,
                                answer: params.answer,
                                checkRef: params.checkRef,
                                itemRef: params.itemRef
                            })
                        }
                    }
                })
            } catch (error) {
                console.error('Error al llamar al endpoint applyrecurrent:', error)
            }
        } else {
            if (context.rootGetters.getOffline) {
                applyRecurrentAPIParams.action = 'applyrecurrent'
                return context.dispatch('syncAction', applyRecurrentAPIParams)
            } else {
                context.commit('setReset', {
                    check: params.check,
                    answer: params.answer,
                    getters: context.getters,
                    cType: params.cType,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef
                })

                // only when answered item was an Action Plan item
                if (checklistType === 5) {
                    context.commit('updateActionPlanPercentage', {
                        cType: params.cType,
                        check: params.check,
                        answer: params.answer,
                        checkRef: params.checkRef,
                        itemRef: params.itemRef
                    })
                }
            }
        }
    },
    async reset(context, params) {
        const canWorkOffline = context.rootGetters.getOffline
        const checklistType = parseInt(params.cType)

        if (context.rootGetters.getOffline || fnCheckConnection()) {
            context.commit('setReset', {
                check: params.check,
                answer: params.answer,
                getters: context.getters,
                cType: params.cType,
                checkRef: params.checkRef,
                itemRef: params.itemRef,
                emp: params.emp,
                date: params.date
            })

            // only when answered item was an Action Plan item
            if (checklistType === 5) {
                context.commit('updateActionPlanPercentage', {
                    cType: params.cType,
                    check: params.check,
                    answer: params.answer,
                    checkRef: params.checkRef,
                    itemRef: params.itemRef
                })

                context.dispatch('actionPlanCalcScore', params.audit)
            }

            let paramsApi

            // for Audit item (not Action Plan item)
            if (checklistType === 2) {
                await context.dispatch('calcScore', params.check)

                let subtask = context.getters.getSubtasks(params.check, params.answer)

                if (subtask.length > 0) {
                    Object.keys(subtask).map(function (item_id, i) {
                        var item = subtask[item_id]

                        context.commit('setReset', {
                            check: params.check,
                            answer: item,
                            getters: context.getters,
                            cType: params.cType,
                            checkRef: params.checkRef,
                            itemRef: params.itemRef
                        })
                    })
                }

                let checklist = context.getters.getById(params.check)

                const total = checklist.total_score
                const answered = checklist.completed
                const score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
                const resume = { it: 0, is: 0, a: 0, n: 0 }

                for (let id in checklist.items) {
                    let item = checklist.items[id]

                    if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                        if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                        if (item.message) resume.n++ // if item has notes
                        if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                        if (item.alerts && item.alerts.issues.length === 0) resume.a++ // if item has alerts and does not have issue
                    }
                }

                // API PARAMS FOR AUDIT ITEM
                paramsApi = {
                    checklist: params.check,
                    task: params.answer,
                    total: total,
                    answered: answered,
                    score: score,
                    resume: JSON.stringify(resume)
                }
            } else {
                // Action Plan item:
                // API PARAMS FOR ACTION PLAN ITEM

                paramsApi = {
                    checklist: params.check,
                    task: params.answer,
                    audit: params.audit
                }
            }

            if (canWorkOffline) {
                await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'reset' })
            }

            if (fnCheckConnection() && context.rootGetters.getFastConnection) {
                const { response } = await postTaskWithRetry(api, 'checklist/task/reset', paramsApi)

                if (!response.status) {
                    paramsApi.action = 'reset'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        console.error('Error in reset():', error)
                    }

                    if (checklistType === 5) {
                        context.dispatch('actionPlanCalcScore', params.check)
                    }
                }

                return { status: true }
            } else {
                paramsApi.action = 'reset'
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        if (checklistType === 5) {
                            context.dispatch('actionPlanCalcScore', params.check)
                        }
                        return { status: true }
                    })
                    .catch(() => {
                        return { status: true }
                    })
            }
        } else {
            return { status: false }
        }
    },
    async add(context, params) {
        var type = context.state.type
        var oData = { template: params.tpl_id }
        if (params.emp_ref) {
            oData.employee_ref = params.emp_ref
        }
        if (params.checklist_ref) {
            oData.checklist_ref = params.checklist_ref
        }
        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }
        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/add', oData).then(async function (response) {
                context.dispatch('format', { data: response.data })
                await context.dispatch('calcScore', Object.keys(response.data)[0])
                await context.dispatch('actionPlanCalcScore', Object.keys(response.data)[0])

                Object.keys(state.templates).map(function (tpl_id, i) {
                    var tpl = state.templates[tpl_id]

                    if (tpl.id == params.tpl_id) {
                        tpl.num_progress++
                    }
                })
                return response.data[Object.keys(response.data)[0]]
            })
        } else {
            if (canWorkOffline) {
                const sections = JSON.parse(JSON.stringify(context.state.templates[params.tpl_id].sections)) //{ id: { id: 'xx', items: { xx: {item_id:xx , value: null}, xxx:{item_id:xx , value: null} } }
                const items = { ...context.state.templates[params.tpl_id].items }
                // Add to items review= 0, complete_date = false, value = null of each section
                Object.keys(sections).map((section_id) => {
                    const section = sections[section_id]

                    Object.keys(section.items).map((item_id) => {
                        const item = section.items[item_id]
                        item.item_id = item_id
                        item.apply = item.apply ? item.apply : 1
                        item.review = false
                        item.complete_date = false
                        item.value = null
                        item.score = 0
                        item.alerts = JSON.parse(JSON.stringify(item.alerts))
                        item.alerts_triggers = JSON.parse(JSON.stringify(item.alerts))
                        if (parseInt(item.type) === FORMAT.MULTIPLE && item.config && item.config.values) {
                            item.config.values = item.config.values.map((option) => {
                                if (option.c) {
                                    return { v: option.v, c: option.c }
                                } else if (option.v && !option.c) {
                                    return { v: option.v, c: false }
                                } else {
                                    return { v: option, c: false }
                                }
                            })
                        }
                        item.config = item.config ? JSON.stringify(item.config) : false
                        if (item.conditions) {
                            const key = Object.keys(item.conditions.values)
                            item.parent_id = key[0]
                        }
                    })
                    section.score = 0
                    section.total = Object.keys(section.items).length
                })

                const user = store.getters['loginUser/getUserInformation']

                const newAudit = {
                    business_date: moment(),
                    complete_date: false,
                    completed: 0,
                    config: context.state.templates[params.tpl_id].config,
                    created_date: moment(),
                    data: false,
                    def: state.config,
                    employee: { ...user },
                    employee_ref: { ...user },
                    employees: [],
                    id: 'tmpitw' + Math.floor(Date.now() / 1000),
                    sections: JSON.parse(JSON.stringify(sections)),
                    recover: false,
                    score: 0,
                    status: 'progress',
                    template_id: params.tpl_id,
                    title: context.state.templates[params.tpl_id].name,
                    total: items.length,
                    type: context.state.templates[params.tpl_id].type,
                    unanswered: items.length,
                    uncompleted: 0,
                    requireSync: true
                }
                const syncData = {
                    action: 'add',
                    checklist: newAudit.id,
                    employee: newAudit.employee.id,
                    template_ref: params.tpl_id,
                    type: newAudit.type
                }

                store.dispatch('audit/syncAction', syncData)
                const copyOfItems = JSON.parse(JSON.stringify(context.state.items))
                const allItems = { [newAudit.id]: newAudit, ...copyOfItems }

                await context.dispatch('format', { data: allItems })
                await context.dispatch('calcScore', newAudit.id)
                await context.dispatch('actionPlanCalcScore', newAudit.id)
                // context.commit('setNumberPage', 1)

                await db_audits
                    .setItem(newAudit.id, JSON.parse(JSON.stringify(newAudit)))
                    .then(() => {
                        // log('Register added to localForage')
                    })
                    .catch((err) => {
                        logError('Error adding register to localForage', err)
                    })

                const itemToReturn = context.getters.getById(newAudit.id)

                return itemToReturn
            }
        }
    },
    reopen(context, params) {
        const checklist = context.state.items[params.check]
        if (!checklist || checklist.reopening || !checklist.complete_date) {
            return Promise.resolve({ status: false })
        }

        Vue.set(checklist, 'reopening', true)

        return api
            .post('checklist/update', { checklist: params.check, status: 2 })
            .then(async function (response) {
                if (!response.status) {
                    Vue.set(checklist, 'reopening', false)
                    return response
                }

                await context.dispatch(
                    'actionplan/updateOpenActionPlansRadarKPIForAudit',
                    {
                        audit: checklist,
                        delta: -1
                    },
                    { root: true }
                )

                checklist.complete_date = null
                checklist.started = 1
                checklist.status = 'progress'
                Vue.set(checklist, 'reopening', false)

                Object.keys(context.state.templates).forEach((tplId) => {
                    const tpl = context.state.templates[tplId]
                    if (tpl.id === checklist.template_id) {
                        tpl.num_progress++
                        context.commit('setTemplate', tpl)
                    }
                })

                await db_audits.setItem(params.check, JSON.parse(JSON.stringify(checklist)))
                return response
            })
            .catch((error) => {
                Vue.set(checklist, 'reopening', false)
                throw error
            })
    },
    delete(context, params) {
        var oData = {
            checklist: params.check,
            audit: params.audit,
            employee: params.emp,
            status: -1
        }
        if (params.ref_item) {
            oData['ref_item'] = params.ref_item
        }

        return api.post('checklist/update', oData).then(async function (response) {
            if (!response.status) {
                return response
            }

            let checklist = { ...context.state.items[params.check] }
            var tpl = { ...context.state.templates[params.template] }
            context.commit('removeItem', params.check)

            if (checklist.complete_date) {
                await context.dispatch(
                    'actionplan/updateOpenActionPlansRadarKPIForAudit',
                    {
                        audit: checklist,
                        delta: -1
                    },
                    { root: true }
                )
            }

            await db_audits.removeItem(params.check)

            if (checklist.status == 'progress') {
                tpl.num_progress--
                context.commit('setTemplate', tpl)
            }
            return response
        })
    },
    loadStats(context, params) {
        context.commit('resetStats', {})
        var currentDate = moment(params.date)

        // -- Created Audits
        context.dispatch('loadFromDate', { date: currentDate.format('YYYY-MM-DD') }).then(function (response) {
            let result = response.data ? Object.keys(response.data).length : 0
            if (response.data && result > 0) {
                context.commit('setStats', {
                    where: 'activity',
                    key_name: 'created',
                    values: {
                        name: i18n.tc('home.activity.registers_generated', result),
                        value: result
                    }
                })
            }
        })
    },
    // FORMAT ACTION
    format({ state, context, commit, dispatch, rootState }, params = {}) {
        const aItems = {}

        if (typeof params.data !== 'undefined') {
            Object.keys(params.data).map((list_id, e) => {
                var list = params.data[list_id]

                if (list.resume && list.resume.ev) {
                    list.finalScore = {
                        color: list.resume.ev.c,
                        name: list.resume.ev.n
                    }
                }

                delete list.resume

                // Offline cases
                if (list.created_date && typeof list.created_date === 'string') {
                    if (list.created_date.length == 10) {
                        list.created_date = list.created_date * 1000
                    }
                    list.created_date = moment(list.created_date)
                }

                if (list.business_date && typeof list.business_date === 'string') {
                    if (list.business_date.length == 10) {
                        list.business_date = list.business_date * 1000
                    }
                    list.business_date = moment(list.business_date)
                }

                if (list.complete_date && typeof list.complete_date === 'string') {
                    if (list.complete_date.length == 10) {
                        list.complete_date = list.complete_date * 1000
                    }
                    list.complete_date = moment(list.complete_date)
                }

                // Timezone set
                list.business_date = list.business_date ? (moment.isMoment(list.business_date) ? list.business_date : moment(list.business_date * 1000)) : false
                list.created_date = list.created_date ? (moment.isMoment(list.created_date) ? list.created_date : moment(list.created_date * 1000)) : false
                list.complete_date = list.complete_date ? (moment.isMoment(list.complete_date) ? list.complete_date : moment(list.complete_date * 1000)) : false

                if (list.business_date.valueOf() < moment().valueOf() && list.started == 0) list.state = 'past'
                else if (list.business_date.valueOf() > moment().valueOf()) list.state = 'future'
                else list.state = 'intime'

                list.def = state.config
                list.uncompleted = 0
                list.completed = 0
                list.unanswered = 0
                list.total = 0
                list.score = -1
                list.grade = false

                // Loop for all sections
                Object.keys(list.sections).map(function (sec_id, i) {
                    var section = list.sections[sec_id]

                    section.completed = 0
                    section.total = 0
                    section.score = -1

                    // Loop for all items
                    Object.keys(section.items).map(function (item_id, o) {
                        var item = section.items[item_id]

                        // Timezone set
                        item.complete_date = item.complete_date ? (moment.isMoment(item.complete_date) ? item.complete_date : moment(item.complete_date * 1000)) : false
                        if (item.review) {
                            item.review['complete_date'] = item.review.complete_date ? (moment.isMoment(item.review.complete_date) ? item.review.complete_date : moment(item.review.complete_date * 1000)) : false
                        }

                        // If type image - Split value to array
                        if (state.config.item.itemsImage.includes(item.type)) {
                            item.value = item.value && typeof item.value === 'string' ? splitCommasBase64(item.value) : item.value
                        }

                        // for action plan items type photo and gallery

                        if (item.actionplans && Object.values(item.actionplans).length > 0) {
                            const actionPlanId = Object.keys(item.actionplans)[0]

                            for (const key in item.actionplans[actionPlanId].items) {
                                if (state.config.item.itemsImage.includes(item.actionplans[actionPlanId].items[key].type)) {
                                    const value = item.actionplans[actionPlanId].items[key].value

                                    if (value) {
                                        item.actionplans[actionPlanId].items[key].value = typeof value === 'string' ? splitCommasBase64(value) : value
                                    }
                                }
                            }
                        }

                        item.complete_date = item.complete_date
                        item.available = 0

                        if (isNaN(parseInt(item.apply))) {
                            item.apply = item.apply ? 1 : 0
                        }

                        item.apply = parseInt(item.apply)

                        // Is object item.config
                        if (item.config && Object.values(item.config).length > 0 && typeof item.config != 'string') {
                            item.config = JSON.stringify(item.config)
                        }

                        if (item.config && Object.values(item.config) == 0 && typeof item.config != 'string') {
                            item.config = JSON.stringify(item.config)
                        }

                        item.config = item.config ? JSON.parse(item.config) : JSON.parse('{}')

                        section.items[item.item_id] = { ...item }
                        Vue.set(section.items, item.item_id, { ...item })
                        // list.sections[sec_id].items[item.id] = { ...item };
                        // Vue.set(list.sections[sec_id].items, item.id, { ...item });
                    })

                    list.sections[section.section_id] = { ...section }
                    Vue.set(list.sections, section.section_id, { ...section })
                })

                aItems[list.id] = list
            })
        }

        commit('setFormat', { items: aItems })
    },

    /** TO - DO
     * La función calcScore() es demasiado compleja (565+ líneas) y debería
     * dividirse en funciones más pequeñas y especializadas:
     *
     * 1. prepareAuditCalculation() - Preparación inicial de datos
     * 2. calculateAuditStatus() - Determinar estado de auditoría
     * 3. ✅ evaluateItemVisibility() - Lógica de visibilidad condicional (COMPLETADO)
     * 4. processItemAlerts() - Sistema de alertas y mensajes
     * 5. calculateItemScore() - Cálculo de puntaje por item
     * 6. compareFieldValue() - Comparaciones por tipo de campo (unificado)
     * 7. aggregateScores() - Suma de puntajes por niveles
     * 8. orderAndFinalize() - Ordenamiento y cálculos finales
     *
     * Beneficios: Mejor mantenibilidad, testing individual, código reutilizable
     */
    /*
     * @param {Object} context - Contexto de Vuex con state, commit, dispatch, etc.
     * @param {String|undefined} id - ID específico de auditoría a calcular, si no se provee calcula todas
     */
    calcScore({ state, context, commit, dispatch, rootState }, id) {
        const audits = state.items
        const aTemplates = state.templates
        const lastAudits = []

        Object.keys(aTemplates).map(function (tpl_id, o) {
            var template = aTemplates[tpl_id]

            if (template.last_audit) {
                lastAudits.push(template.last_audit.id)
            }
        })

        Object.keys(audits).map((auditId) => {
            const checklist = audits[auditId]
            const template = aTemplates[checklist.template_id]

            if (typeof id === 'undefined' || auditId == id) {
                if (lastAudits.includes(checklist.id)) {
                    template.completed = 0
                    template.total = 0
                    template.total_score = 0
                    template.last_score = 0
                }

                checklist.def = state.config
                checklist.uncompleted = 0
                checklist.completed = 0
                checklist.unanswered = 0
                checklist.total_score = 0
                checklist.total = 0
                checklist.score = 0
                checklist.values_no_visible = false
                checklist.itemsDone = 0

                const noNegativeScoreFlag = checklist.config && checklist.config.score_noneg ? Number(checklist.config.score_noneg) : undefined
                // Si config.score_noneg existe y vale 1, las secciones y la auditoría no pueden terminar con puntuación negativa.
                // Ejemplo: Si la suma de los items de una sección queda por debajo de 0 (p. ej. -2), el valor se ajusta a 0.
                const allowNegativeScore = noNegativeScoreFlag !== 1
                // Por defecto allowNegativeScore es true, a menos que config.score_noneg exista y valga 1.

                // Set status of the Audit
                if (moment(checklist.business_date) > moment()) {
                    checklist.status = 'scheduled'
                } else if (checklist.complete_date) {
                    checklist.status = 'completed'
                } else if (checklist.started == 1) {
                    checklist.status = 'progress'
                }

                // Loop for all sections
                Object.keys(checklist.sections).map((sectionId) => {
                    const section = checklist.sections[sectionId]

                    section.completed = 0
                    section.total = 0
                    section.score = 0
                    section.total_score = 0

                    // Loop for all items
                    Object.keys(section.items).map((itemId, i) => {
                        const item = section.items[itemId]

                        // Subsubitem: item whose parent is itself a subitem (parent has parent_id)
                        const parent = item.parent_id ? section.items[item.parent_id] : null
                        item.is_subsubitem = !!(parent && parent.parent_id)

                        // Esta propiedad se usa para mostrar o no el item o SUBITEM en el checklist
                        // Si es un item padre, se muestra siempre
                        // Si es un subitem, este se muestra si la respuesta al item padre cumple las condiciones configuradas para el subitem
                        if (!item.parent_id) {
                            item.is_visible = true
                        } else {
                            item.is_visible = evaluateItemVisibility(item, section)
                        }

                        // Si es un subitem (tiene condiciones), ajustar su orden después del item padre
                        if (typeof item.conditions !== 'undefined' && item.conditions != null && item.conditions) {
                            // Encontrar el orden del item padre
                            let parentItemOrder = item.parent_id && section.items[item.parent_id] ? parseFloat(section.items[item.parent_id].order) : -1
                            const conditions = item.conditions.values
                            for (const key in conditions) {
                                const parentConditionItem = section.items[key]
                                if (parentItemOrder === -1 && parentConditionItem) {
                                    parentItemOrder = parseFloat(parentConditionItem.order)
                                }
                                break // Solo necesitamos el primer padre para el orden
                            }

                            // Ajustar order basado en el item padre
                            const subitemOrder = getBaseOrder(item)
                            if (!Number.isNaN(parentItemOrder) && parentItemOrder !== -1 && !Number.isNaN(subitemOrder)) {
                                item.order = parentItemOrder + subitemOrder / 10000
                            } else {
                                item.order = subitemOrder
                            }
                        }

                        if (!checklist.values_no_visible) {
                            if (item.value !== null && item && !item.is_visible) {
                                checklist.values_no_visible = true
                            }
                        }

                        // SHOW ALERTS
                        // se setea la propiedad alerts del item según si el valor del item cumple las condiciones de los triggers
                        const oAlert = buildItemAlerts(item, FORMAT)

                        if (oAlert) {
                            item.alerts = oAlert
                            Vue.set(item, 'alerts', oAlert)
                        } else {
                            item.alerts = false
                        }

                        // CALC SCORE PROPIO DE AUDITS
                        if (((!item.parent_id && checklist.def.item.itemsExcluded.indexOf(item.type) == -1) || (item.parent_id && item.is_visible)) && !item.planned_to_date && item.apply) {
                            item.score = 0
                            let scoreTotal = item.param && item.param.total ? item.param.total : 0
                            let conditions = item.param && item.param.conditions ? item.param.conditions : []
                            const itemValue = item.value
                            let shouldScore = false

                            for (var i in conditions) {
                                let param = conditions[i]
                                const operator = param.operator ? param.operator : '='
                                const conditionValue = param.value
                                const valueScore = param.p

                                if ((itemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(item.type) || !item.apply) && item.is_visible) {
                                    switch (item.type) {
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            shouldScore = compare(parseInt(conditionValue) == 1, itemValue == true || itemValue == 'true', operator)
                                            break
                                        case FORMAT.YESNONC:
                                            shouldScore = compare(conditionValue || (conditionValue == '' && operator == '!empty'), itemValue, operator)
                                            break
                                        case FORMAT.MULTIPLE:
                                            const itemIsMultiselect = item && item.config && item.config.hasOwnProperty('multiselect') && item.config.multiselect === 1

                                            if (!itemIsMultiselect) {
                                                // SINGLE SELECT
                                                shouldScore = compare(conditionValue || (conditionValue === '' && operator === '!empty'), itemValue, operator)
                                            } else {
                                                // MULTIPLE SELECT
                                                if (Array.isArray(itemValue) || (typeof itemValue === 'string' && itemValue.includes('|'))) {
                                                    const itemValues = Array.isArray(itemValue) ? itemValue : itemValue.split('|')
                                                    shouldScore = itemValues.some((value) => compare(conditionValue || (conditionValue === '' && operator === '!empty'), value, operator))
                                                } else {
                                                    shouldScore = compare(conditionValue || (conditionValue === '' && operator === '!empty'), itemValue, operator)
                                                }
                                            }

                                            break
                                        case FORMAT.CHECK:
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            shouldScore = compare(conditionValue, itemValue, operator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = itemValue.toString().replace(',', '.')
                                            shouldScore = compare(parseFloat(aux), conditionValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = itemValue.toString().replace(',', '.')
                                            shouldScore = compare(parseFloat(aux), conditionValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        default:
                                            break
                                    }

                                    if (shouldScore) {
                                        item.score = valueScore
                                        section.completed = parseFloat(section.completed) + parseFloat(item.score)
                                        checklist.completed = parseFloat(checklist.completed) + parseFloat(item.score)
                                    }
                                }
                            }
                            // Posible total score
                            checklist.total_score = parseFloat(checklist.total_score) + parseFloat(scoreTotal)
                            section.total_score = parseFloat(section.total_score) + parseFloat(scoreTotal)

                            // Total tal cual de items
                            if (![FORMAT.LINK, FORMAT.RESOURCE].includes(item.type)) {
                                checklist.total++
                                section.total++
                            }
                        }

                        // Calculate checklist.itemsDone
                        // Only count visible items
                        if (item.is_visible && !checklist.def.item.itemsExcluded.includes(item.type)) {
                            // Check if item has a value (is answered)
                            if (item.value !== null && item.value !== undefined && item.value !== '') {
                                if (item.require_review) {
                                    checklist.itemsDone += item.review ? 1 : 0.5
                                } else {
                                    checklist.itemsDone += 1
                                }
                            }
                        }

                        section.items[item.item_id] = { ...item }
                        Vue.set(section.items, item.item_id, { ...item })
                    })

                    section.score = Math.round((parseFloat(section.completed) * 100) / parseFloat(section.total_score))
                    if (!allowNegativeScore && section.completed < 0) {
                        section.completed = 0
                    }

                    if (lastAudits.includes(checklist.id)) {
                        template.total_score = parseFloat(template.total_score) + parseFloat(checklist.total_score)
                        template.total = parseFloat(template.total) + parseFloat(checklist.total)
                        template.completed = parseFloat(template.completed) + parseFloat(checklist.completed)
                    }

                    checklist.sections[section.section_id] = { ...section }
                    Vue.set(checklist.sections, section.section_id, { ...section })
                })

                let sectionCompleted = 0
                let sectionTotalScore = 0

                for (var key in checklist.sections) {
                    const section = checklist.sections[key]
                    sectionCompleted += section.completed
                    sectionTotalScore += section.total_score
                }

                checklist.score = sectionTotalScore == 0 ? 0 : Math.round((parseFloat(sectionCompleted) * 100) / parseFloat(sectionTotalScore))

                if (lastAudits.includes(checklist.id)) {
                    template.last_score = parseFloat(template.last_score) + parseFloat(checklist.score)
                }

                // aItems[list.id] = { ...list }
                audits[checklist.id] = checklist
            }
        })

        // ORDER
        Object.keys(audits).map(function (item_id, i) {
            let list = JSON.parse(JSON.stringify(audits[item_id]))

            Object.keys(list.sections).map(function (sec_id, o) {
                var section = list.sections[sec_id]
                section.items = buildHierarchicalOrderedItems(section.items)
            })

            audits[item_id] = list
        })

        Object.keys(aTemplates).map(function (tpl_id, o) {
            var template = aTemplates[tpl_id]

            template.total_score = parseFloat(template.total_score) == 0 ? 0 : Math.round((parseFloat(template.completed) * 100) / parseFloat(template.total_score))
        })

        commit('setTemplates', { data: aTemplates })
        commit('setScore', { items: audits })
    },
    // ACTION PLAN CALCSCORE
    actionPlanCalcScore({ state, context, commit, dispatch, rootState }, id) {
        const audits = state.items
        const aTemplates = state.templates
        const lastAudits = []

        Object.keys(aTemplates).map((templateId) => {
            const template = aTemplates[templateId]

            if (template.last_audit) {
                lastAudits.push(template.last_audit.id)
            }
        })

        Object.keys(audits).map((auditId) => {
            const audit = audits[auditId]

            if (typeof id === 'undefined' || auditId === id) {
                // Loop for all sections
                Object.keys(audit.sections).map((sectionId) => {
                    const section = audit.sections[sectionId]

                    // Loop for all AUDIT items
                    Object.keys(section.items).map((itemId) => {
                        const item = section.items[itemId]

                        const actionPlans = item.actionplans

                        if ((Array.isArray(actionPlans) && actionPlans.length > 0) || (!Array.isArray(actionPlans) && Object.values(actionPlans).length > 0)) {
                            const actionplan = Object.values(item.actionplans)[0]

                            // Loop all actionplan's items
                            Object.keys(actionplan.items).map((itemId) => {
                                const actionPlanItem = actionplan.items[itemId]

                                // SHOW SUBITEMS (visibilidad de los items de plan de acción)
                                // Si es un item padre, se muestra siempre
                                // Si es un subitem, este se muestra si la respuesta al item padre cumple las condiciones configuradas para el subitem
                                if (!actionPlanItem.parent_id) {
                                    actionPlanItem.is_visible = true
                                } else {
                                    // Crear una sección temporal con los items del action plan
                                    // organizados por item_id para que evaluateItemVisibility los pueda encontrar
                                    const actionPlanSection = {
                                        items: {}
                                    }

                                    // Asegurar que los items estén indexados por item_id
                                    Object.values(actionplan.items).forEach((item) => {
                                        actionPlanSection.items[item.item_id] = item
                                    })

                                    actionPlanItem.is_visible = evaluateItemVisibility(actionPlanItem, actionPlanSection)
                                }

                                // SHOW ALERTS
                                // se setea la propiedad alerts del item según si el valor del item cumple las condiciones de los triggers
                                const oAlert = buildItemAlerts(actionPlanItem, FORMAT)

                                if (oAlert) {
                                    actionPlanItem.alerts = oAlert
                                    Vue.set(actionPlanItem, 'alerts', oAlert)
                                } else {
                                    actionPlanItem.alerts = false
                                }

                                if (((!actionPlanItem.parent_id && audit.def.item.itemsExcluded.indexOf(actionPlanItem.type) === -1) || (actionPlanItem.parent_id && actionPlanItem.is_visible)) && !actionPlanItem.planned_to_date && actionPlanItem.apply) {
                                    actionPlanItem.score = 0
                                    let scoreTotal = actionPlanItem.param && actionPlanItem.param.total ? actionPlanItem.param.total : 0

                                    // Posible total score
                                    audit.total_score = parseFloat(audit.total_score) + parseFloat(scoreTotal)
                                    actionplan.total_score = parseFloat(actionplan.total_score) + parseFloat(scoreTotal)

                                    // Total tal cual de items
                                    if (![FORMAT.LINK, FORMAT.RESOURCE].includes(actionPlanItem.type)) {
                                        audit.total++
                                        actionplan.total++
                                    }
                                }

                                // CALC SCORE PROPIO DE AUDITS
                                if (((!actionPlanItem.parent_id && audit.def.item.itemsExcluded.indexOf(actionPlanItem.type) == -1) || (actionPlanItem.parent_id && actionPlanItem.is_visible)) && !actionPlanItem.planned_to_date && actionPlanItem.apply) {
                                    actionPlanItem.score = 0
                                    let scoreTotal = actionPlanItem.param && actionPlanItem.param.total ? actionPlanItem.param.total : 0

                                    // Posible total score
                                    audit.total_score = parseFloat(audit.total_score) + parseFloat(scoreTotal)
                                    actionplan.total_score = parseFloat(actionplan.total_score) + parseFloat(scoreTotal)

                                    // Total tal cual de items
                                    if (![FORMAT.LINK, FORMAT.RESOURCE].includes(actionPlanItem.type)) {
                                        audit.total++
                                        actionplan.total++
                                    }
                                }

                                actionplan.items[actionPlanItem.item_id] = { ...actionPlanItem }
                                Vue.set(actionplan.items, actionPlanItem.item_id, { ...actionPlanItem })
                            })
                        }
                    })

                    audit.sections[section.section_id] = { ...section }
                    Vue.set(audit.sections, section.section_id, { ...section })
                })

                audits[audit.id] = audit

                commit('updateActionPlanPercentage', {
                    checkRef: audit.id
                })
            }
        })

        // ORDER
        Object.keys(audits).map(function (item_id, i) {
            let list = JSON.parse(JSON.stringify(audits[item_id]))

            Object.keys(list.sections).map(function (sec_id, o) {
                var section = list.sections[sec_id]
                section.items = buildHierarchicalOrderedItems(section.items)
            })

            audits[item_id] = list
        })

        Object.keys(aTemplates).map(function (tpl_id, o) {
            var template = aTemplates[tpl_id]

            template.total_score = parseFloat(template.total_score) == 0 ? 0 : Math.round((parseFloat(template.completed) * 100) / parseFloat(template.total_score))
        })

        commit('setTemplates', { data: aTemplates })
        commit('setScore', { items: audits })
        // commit('setActionPlanTemplates', )
    },
    async loadItems(context, params) {
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            if (params.noRefresh) {
                const items = context.getters.getItems
                context.dispatch('format', { data: items })
                await context.dispatch('calcScore')
                await context.dispatch('actionPlanCalcScore')
                context.commit('setNumberItems', items.length)
                context.commit('setNumberPage', context.state.number_page)
                return
            }

            if (!fnCheckConnection()) {
                return loadItemsOffline(context.commit, context.dispatch)
            }

            context.commit('setNumberPage', context.state.number_page)

            return api
                .post('checklist/resume/' + params.template, {
                    number_pages: context.state.number_page,
                    number_items: 20,
                    type: state.type
                })
                .then(async function (response) {
                    // Validar que la respuesta tenga la estructura esperada
                    if (!response || !response.data || !response.data.values) {
                        console.error('Invalid response structure:', response)
                        return loadItemsOffline(context.commit, context.dispatch)
                    }

                    _.forEach(response.data.values, async function (value, key) {
                        await db_audits.setItem(value.id, JSON.parse(JSON.stringify(value)))
                    })

                    context.dispatch('format', { data: response.data.values })
                    await context.dispatch('calcScore')
                    await context.dispatch('actionPlanCalcScore')
                    context.commit('setResume', { data: response.data.schema })
                    context.commit('setNumberItems', response.data.number_items)
                    context.commit('setNumberPage', context.state.number_page)
                    if (response.data.open_count !== undefined) {
                        context.commit('setOpenCount', response.data.open_count)
                    }
                })
                .catch((error) => {
                    console.error('Error in loadItems():', error)
                    return loadItemsOffline(context.commit, context.dispatch)
                })
        } else if (!params.noRefresh) {
            await context.dispatch('calcScore')
            await context.dispatch('actionPlanCalcScore')
            return loadItemsOffline(context.commit, context.dispatch)
        }
    },
    changeSupervisor({ state, context, commit, dispatch, rootState }, params = {}) {
        commit('setSupervisor', { id: params.emp_id })
    },
    loadFromDate({ state, context, commit, dispatch, rootState }, params = {}) {
        params.date = params.date ? params.date : moment().format('YYYY-MM-DD')
        return api.get('checklist/' + state.type + '/' + params.date, {}).then(function (response) {
            return response
        })
    },
    recoverValues(context, params) {
        return api.post('checklist/recover/' + params.check, {}).then(async function (response) {
            if (response.status) {
                context.dispatch('format', { data: response.data })
                await context.dispatch('calcScore', params.id)
                await context.dispatch('actionPlanCalcScore', params.id)
            } else {
                router.go()
            }

            return response.data
        })
    },
    syncAction(context, params) {
        params.queued_date = moment().valueOf()
        params.id = moment().valueOf()

        return context.dispatch('checklist/addAuditToQueue', params, { root: true }).then(() => {
            return { status: true }
        })
    },
    sync(context, queue) {
        let checklists = {}
        for (let key in queue) {
            let queueElement = queue[key]
            if (typeof checklists[queueElement.checklist] === 'undefined') {
                checklists[queueElement.checklist] = {
                    items: [],
                    resume: { it: 0, is: 0, a: 0, n: 0 },
                    total: 0,
                    answered: 0,
                    score: 0
                }
            }

            checklists[queueElement.checklist].resume = queueElement.resume
            checklists[queueElement.checklist].total = queueElement.total
            checklists[queueElement.checklist].answered = queueElement.answered
            checklists[queueElement.checklist].score = queueElement.score
            checklists[queueElement.checklist].items.push(queueElement)
        }
        return api
            .post(`checklist/${state.type}/sync`, { checklists: JSON.stringify(checklists) })
            .then(function (response) {
                if (!response.status) {
                    throw new Error('API error') //Por si falla algo de email
                }
                if (response.data) {
                    const issues = response.data.filter((queue) => queue.issue_id)
                    if (issues.length > 0) {
                        context.dispatch('updateIssueOfflineDatabase', issues)
                        response.data = response.data.filter((queue) => !queue.issue_id)
                    }
                }
                return context
                    .dispatch('updateOfflineDatabase', response)
                    .then(() => {
                        return response
                    })
                    .catch(function (error) {
                        logError(error)
                        return false
                    })
            })
            .catch(function (error) {
                logError(error)
                return false
            })
    },
    async updateIssueOfflineDatabase(context, response) {
        if (response) {
            const checklists = response
            checklists.forEach(async (checklist) => {
                //Actualizar el estado de sincronización de los registros
                await store.dispatch('issues/updateStateOfSync', checklist)

                // Actualizar el id de sincronización de los registros
                if (checklist && checklist.checklist_temporal && checklist.checklist) {
                    const register = await db_issues.getItem(checklist.checklist_temporal)
                    if (register) {
                        register.id = checklist.checklist
                        register.issue_id = checklist.issue_id
                        await db_issues.removeItem(checklist.checklist_temporal)
                        await db_issues.setItem(checklist.checklist, register)
                    }
                    //change the key of the item and id
                    const copy = store.getters['issues/getAll']
                    copy[checklist.checklist_temporal].id = checklist.checklist
                    copy[checklist.checklist_temporal].requireSync = false
                    copy[checklist.checklist_temporal].issue_id = checklist.issue_id
                    copy[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const updateItem = { [checklist.checklist]: copy[checklist.checklist_temporal] }
                    delete copy[checklist.checklist_temporal]

                    const allItems = { ...updateItem, ...copy }
                    /* context.state.items = {...copy} */
                    await store.dispatch('issues/format', { data: allItems })

                    //update allItems
                    const newIssueItem = store.getters['issues/getAll'][copy.status]
                    newIssueItem[checklist.checklist_temporal].id = checklist.checklist
                    newIssueItem[checklist.checklist_temporal].requireSync = false
                    newIssueItem[checklist.checklist_temporal].issue_id = checklist.issue_id
                    newIssueItem[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const newAllItems = { ...newIssueItem }
                    store.commit('issues/setAllItems', { items: newAllItems, type: copy.status })
                }
            })
        }
    },
    async mergeOffline(context, params = {}) {
        await db_audits_queue.iterate(function (answer, checklistId) {
            if (typeof params.data[answer.checklist] !== 'undefined') {
                for (let sectionId in params.data[answer.checklist].sections) {
                    if (typeof params.data[answer.checklist].sections[sectionId].items[answer.task] !== 'undefined') {
                        params.data[answer.checklist].sections[sectionId].items[answer.task].employee = answer.employee
                        params.data[answer.checklist].sections[sectionId].items[answer.task].complete_date = answer.complete_date
                        params.data[answer.checklist].sections[sectionId].items[answer.task].value = answer.value ? answer.value.toString() : answer.value
                        params.data[answer.checklist].sections[sectionId].items[answer.task].requireSync = true
                    }
                }
            }
        })
        return params
    },
    async clearOfflineQueue(context, params) {
        const data = params.data
        const action = params.action
        //['answer', 'reset', 'validate', 'unvalidate', 'complete', 'uncomplete']
        const mapActionsToRemove = {
            answer: ['answer', 'reset'],
            reset: ['answer', 'reset'],
            validate: ['validate', 'unvalidate'],
            unvalidate: ['validate', 'unvalidate']
        }

        const actionsToRemove = mapActionsToRemove[action]

        await db_audits_queue.iterate(async function (answer, key, iterationNumber) {
            const isTheSameChecklist = answer.checklist === data.checklist && answer.task === data.task
            if (actionsToRemove.includes(answer.action) && isTheSameChecklist) {
                await db_audits_queue.removeItem(key)
            }
        })
    },
    async updateStateOfSync(context, params) {
        // Actualizar el estado de sincronización de los registros
        if (params && params.checklist_temporal) {
            const register = await db_audits.getItem(params.checklist_temporal)
            if (register) {
                register.requireSync = false
                await db_audits.setItem(params.checklist_temporal, register)
            }
            state.items[params.checklist_temporal].requireSync = false
        }
    },

    async updateOfflineDatabase(context, response) {
        if (response.data) {
            if (response.data.type && response.data.type !== 'add') return

            const checklists = response.data
            checklists.forEach(async (checklist) => {
                //Actualizar el estado de sincronización de los registros
                await context.dispatch('updateStateOfSync', checklist)

                // Actualizar el id de sincronización de los registros
                if (checklist && checklist.checklist_temporal && checklist.checklist) {
                    const register = await db_audits.getItem(checklist.checklist_temporal)
                    if (register) {
                        register.id = checklist.checklist
                        await db_audits.removeItem(checklist.checklist_temporal)
                        await db_audits.setItem(checklist.checklist, register)
                    }
                    //change the key of the item and id
                    const copy = { ...context.state.items }
                    copy[checklist.checklist_temporal].id = checklist.checklist
                    copy[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const updateItem = { [checklist.checklist]: copy[checklist.checklist_temporal] }
                    delete copy[checklist.checklist_temporal]

                    const allItems = { ...updateItem, ...copy }
                    context.state.items = { ...copy }
                    await context.dispatch('format', { data: allItems })
                }
            })
        }
    }
}

const mutations = {
    setIsLoading(state, payload) {
        state.isLoading = payload
    },
    setTemplatesLoading(state, payload) {
        state.templatesLoading = payload
    },
    setTemplatesLoaded(state, payload) {
        state.templatesLoaded = payload
    },
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setTemplates(state, payload) {
        var templates = {}

        if (payload.data != undefined) {
            Object.keys(payload.data).map(function (tpl_id, i) {
                var tpl = { ...payload.data[tpl_id] }

                tpl.last_update = tpl.last_update ? (moment.isMoment(tpl.last_update) ? tpl.last_update : moment(tpl.last_update * 1000)) : false

                tpl.last_score = tpl.last_score && !Number.isNaN(tpl.last_score) ? tpl.last_score : 0
                tpl.total_score = tpl.total_score && !Number.isNaN(tpl.total_score) ? tpl.total_score : 0
                tpl.total = tpl.total && !Number.isNaN(tpl.total) ? tpl.total : 0
                tpl.completed = tpl.completed && !Number.isNaN(tpl.completed) ? tpl.completed : 0

                templates[tpl.id] = tpl
            })
        }

        state.templates = templates
        Vue.set(state, 'templates', { ...templates })
    },
    setActionPlanTemplates(state, payload) {
        state.actionPlanTemplates = payload.data
    },
    setTemplate(state, template) {
        var templates = { ...state.templates }
        templates[template.id] = template

        state.templates = templates
        Vue.set(state, 'templates', { ...templates })
    },
    setItem(state, item) {
        var items = { ...state.items }
        items[item.id] = item

        state.items = items
        Vue.set(state, 'items', { ...items })
    },
    removeItem(state, id) {
        var items = { ...state.items }
        delete items[id]

        state.items = items
        Vue.set(state, 'items', { ...items })
    },
    resetStats(state, payload) {
        state.stats.alerts = {}
        state.stats.activity = {}
    },
    setStats(state, payload) {
        state.stats[payload.where] = {}
        state.stats[payload.where][payload.key_name] = payload.values
    },
    setFormat(state, payload) {
        state.items = payload.items
        Vue.set(state, 'items', { ...payload.items })
    },
    setScore(state, payload) {
        state.items = payload.items
        Vue.set(state, 'items', { ...payload.items })
    },
    setAnswerProp(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var prop = payload.prop
        var value = payload.value
        var section = payload.getters['getSectionFromItem'](check, answer)

        state.items[check].sections[section].items[answer][prop] = value

        Vue.set(state, 'items', { ...state.items })
    },
    setComment(state, payload) {
        let check = payload.check
        let answer = payload.answer
        var section = payload.getters['getSectionFromItem'](check, answer)

        let aux = state.items[check].sections[section].items[answer]
        aux.message = payload.message
        aux.image = payload.image ? payload.image : false
        aux.requireSync = payload.requireSync

        Vue.set(state.items[check].sections[section].items, answer, { ...aux })
        Vue.set(state, 'items', { ...state.items })
    },
    async setNumberItems(state, payload) {
        state.number_items = payload
        Vue.set(state, 'number_items', state.number_items)
        await db_templates.setItem('audits_items', payload)
    },
    async setNumberPage(state, payload) {
        state.number_page = payload
        Vue.set(state, 'number_page', state.number_page)
        await db_templates.setItem('audits_page', payload)
    },
    setOpenCount(state, payload) {
        state.open_count = payload || 0
        Vue.set(state, 'open_count', state.open_count)
    },
    setAnswer(state, payload) {
        const checklistType = parseInt(payload.cType)
        const auditId = checklistType === 2 ? payload.check : payload.checkRef
        const auditItemId = checklistType === 2 ? payload.answer : payload.itemRef
        const value = payload.value
        const employeeId = payload.emp
        const date = payload.date
        const auditSectionId = payload.getters['getSectionFromItem'](auditId, auditItemId)
        const bReadingFromSensor = payload.bReadingFromSensor

        // set Audit item value
        if (checklistType === 2) {
            state.items[auditId].sections[auditSectionId].items[auditItemId].value = value
            state.items[auditId].sections[auditSectionId].items[auditItemId].requireSync = payload.requireSync
            state.items[auditId].sections[auditSectionId].items[auditItemId].readingFromSensor = bReadingFromSensor
            if (date) {
                const item = state.items[auditId].sections[auditSectionId].items[auditItemId]
                item.complete_date = !moment.isMoment(date) && date !== false ? moment(date * 1000) : item.complete_date
            }
            if (employeeId) {
                state.items[auditId].sections[auditSectionId].items[auditItemId].employee = employeeId
            }
        }

        // set Action Plan item value
        if (checklistType === 5) {
            // for Action Plan Item:
            const actionPlanId = payload.check
            const itemId = payload.answer

            state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].value = value
            state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].requireSync = payload.requireSync
            state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].readingFromSensor = bReadingFromSensor

            if (date) {
                const item = state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId]
                item.complete_date = !moment.isMoment(date) && date !== false ? moment(date * 1000) : item.complete_date
            }
            if (employeeId) {
                state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].employee = employeeId
            }
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setApply(state, payload) {
        const checklistType = payload.cType
        const auditId = checklistType === 2 ? payload.check : payload.checkRef
        const auditItemId = checklistType === 2 ? payload.answer : payload.itemRef
        const employeeId = payload.emp
        const apply = parseInt(payload.apply)
        const date = payload.date
        const auditSectionId = payload.sectionId ? payload.sectionId : payload.getters['getSectionFromItem'](auditId, auditItemId)

        // only for Audit item (not Action Plan item)
        if (checklistType === 2) {
            // Validación de seguridad antes de acceder a la estructura
            if (!state.items[auditId]) {
                console.error('state.items[auditId] es undefined. auditId:', auditId)
                return
            }
            if (!state.items[auditId].sections) {
                console.error('state.items[auditId].sections es undefined')
                return
            }
            if (!state.items[auditId].sections[auditSectionId]) {
                console.error('state.items[auditId].sections[auditSectionId] es undefined. auditSectionId:', auditSectionId)
                return
            }
            if (!state.items[auditId].sections[auditSectionId].items) {
                console.error('state.items[auditId].sections[auditSectionId].items es undefined')
                return
            }
            // Si auditItemId es null, es un apply a nivel de sección completa
            if (auditItemId === null) {
                const sectionItems = state.items[auditId].sections[auditSectionId].items
                Object.keys(sectionItems).forEach((itemId) => {
                    if (sectionItems[itemId] && !sectionItems[itemId].mandatory) {
                        sectionItems[itemId].apply = apply
                        sectionItems[itemId].value = null

                        const itemCompletedDate = sectionItems[itemId].complete_date
                        const itemEmployee = sectionItems[itemId].employee

                        // for "Available" button (sets date and employee back to false)
                        if (itemCompletedDate !== false && itemEmployee !== false && apply === 1) {
                            sectionItems[itemId].complete_date = false
                            sectionItems[itemId].employee = false
                        } else if (date) {
                            const completedDate = sectionItems[itemId].complete_date
                            sectionItems[itemId].complete_date = !moment.isMoment(date) && date !== false ? moment(date * 1000) : completedDate
                            sectionItems[itemId].employee = employeeId || false
                        }
                    }
                })
                return // Salir después de procesar toda la sección
            }

            if (!state.items[auditId].sections[auditSectionId].items[auditItemId]) {
                console.error('state.items[auditId].sections[auditSectionId].items[auditItemId] es undefined. auditItemId:', auditItemId)
                return
            }

            if (auditItemId) {
                // set item value and item apply
                state.items[auditId].sections[auditSectionId].items[auditItemId].apply = apply
                state.items[auditId].sections[auditSectionId].items[auditItemId].value = null

                const itemCompletedDate = state.items[auditId].sections[auditSectionId].items[auditItemId].complete_date
                const itemEmployee = state.items[auditId].sections[auditSectionId].items[auditItemId].employee

                // for "Available" button (sets date and employee back to false)
                if (itemCompletedDate !== false && itemEmployee !== false && apply === 1) {
                    state.items[auditId].sections[auditSectionId].items[auditItemId].complete_date = false
                    state.items[auditId].sections[auditSectionId].items[auditItemId].employee = false
                } else {
                    // for "Not apply" button: set date and employee to display it in item dropdown info
                    if (date) {
                        const completedDate = state.items[auditId].sections[auditSectionId].items[auditItemId].complete_date

                        state.items[auditId].sections[auditSectionId].items[auditItemId].complete_date = !moment.isMoment(date) && date !== false ? moment(date * 1000) : completedDate
                        state.items[auditId].sections[auditSectionId].items[auditItemId].value = null
                    } else {
                        state.items[auditId].sections[auditSectionId].items[auditItemId].complete_date = false
                    }

                    if (employeeId) {
                        state.items[auditId].sections[auditSectionId].items[auditItemId].employee = employeeId
                    }
                }
            }
        }

        // only for Action Plan items
        if (checklistType === 5) {
            const actionPlanId = payload.check
            const itemId = payload.answer

            // set item value and item apply
            state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].apply = apply
            state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].value = null

            const itemCompletedDate = state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].complete_date
            const itemEmployee = state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].employee

            // for "Available" button (sets date and employee back to false)
            if (itemCompletedDate !== false && itemEmployee !== false && apply === 1) {
                state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].complete_date = false
                state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].employee = false
            } else {
                // for "Not apply" button: set date and employee to display it in item dropdown info
                if (date) {
                    const item = state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId]
                    item.complete_date = !moment.isMoment(date) && date !== false ? moment(date * 1000) : item.complete_date
                }

                if (employeeId) {
                    state.items[auditId].sections[auditSectionId].items[auditItemId].actionplans[actionPlanId].items[itemId].employee = employeeId
                }
            }
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setReset(state, payload) {
        if (payload.getters !== undefined) {
            const checklistType = payload.cType // 2 = audit, 5 = action plan
            const auditId = checklistType === 2 ? payload.check : payload.checkRef
            const auditItemId = checklistType === 2 ? payload.answer : payload.itemRef
            const auditSectionId = payload.getters['getSectionFromItem'](auditId, auditItemId)
            const emp = payload.emp
            const date = payload.date

            if (!auditSectionId || !state.items[auditId] || !state.items[auditId].sections || !state.items[auditId].sections[auditSectionId]) {
                return
            }

            // reset Audit item
            if (checklistType === 2) {
                const auditItem = state.items[auditId].sections[auditSectionId].items[auditItemId]
                if (!auditItem) {
                    return
                }
                auditItem.complete_date = false
                auditItem.employee = false
                auditItem.review = false
                auditItem.value = null
                if (emp && date) {
                    auditItem.reset = {
                        e: emp,
                        d: date
                    }
                }
            }

            // reset Action Plan item
            if (checklistType === 5) {
                const actionPlanId = payload.check
                const itemId = payload.answer
                const auditItem = state.items[auditId].sections[auditSectionId].items[auditItemId]
                if (!auditItem || !auditItem.actionplans || !auditItem.actionplans[actionPlanId] || !auditItem.actionplans[actionPlanId].items || !auditItem.actionplans[actionPlanId].items[itemId]) {
                    return
                }

                auditItem.actionplans[actionPlanId].items[itemId].value = null
                auditItem.actionplans[actionPlanId].items[itemId].complete_date = false
                auditItem.actionplans[actionPlanId].items[itemId].employee = false
                auditItem.actionplans[actionPlanId].items[itemId].review = false
                if (emp && date) {
                    auditItem.actionplans[actionPlanId].items[itemId].reset = {
                        e: emp,
                        d: date
                    }
                }
            }

            Vue.set(state, 'items', { ...state.items })
        }
    },
    setValidation(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var date = payload.date
        var section = payload.getters['getSectionFromItem'](check, answer)

        state.items[check].sections[section].items[answer].review = {}
        if (date) {
            var item = state.items[check].sections[section].items[answer].review
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) {
            var employee = store.getters['employee/getEmployee'](emp)
            state.items[check].sections[section].items[answer].review.employee = employee
        }
        state.items[check].sections[section].items[answer].review.value = 1

        Vue.set(state, 'items', { ...state.items })
    },
    setUnvalidation(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var section = payload.getters['getSectionFromItem'](check, answer)

        state.items[check].sections[section].items[answer].review = false

        Vue.set(state, 'items', { ...state.items })
    },
    setResume(state, payload) {
        var aItems = {}

        Object.keys(payload.data).map(function (id, i) {
            var item = payload.data[id]

            if (item.type == '8') {
                aItems[item.id] = item
            }
        })

        state.headers = aItems
        Vue.set(state, 'headers', aItems)
    },
    setSupervisor(state, payload) {
        state.supervisor = payload.id
        Vue.set(state, 'supervisor', payload.id)
    },
    setRequireSync(state, payload) {
        let check = payload.check
        let answer = payload.answer
        let apiId = payload.apiId

        check = state.items[check] ? check : state.items[apiId] ? apiId : false

        if (typeof state.items[check] !== 'undefined') {
            for (let sectionId in state.items[check].sections) {
                if (typeof state.items[check].sections[sectionId].items[answer] !== 'undefined') {
                    state.items[check].sections[sectionId].items[answer].requireSync = payload.requireSync
                    state.items[check].sections[sectionId].items[answer] = {
                        ...state.items[check].sections[sectionId].items[answer]
                    }
                }
            }
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setActionPlanStatus(state, payload) {
        if (!payload || !payload.actionPlanId) {
            return
        }

        const actionPlanId = String(payload.actionPlanId)

        Object.values(state.items).forEach((audit) => {
            const sections = audit && audit.sections ? Object.values(audit.sections) : []

            sections.forEach((section) => {
                const items = section && section.items ? Object.values(section.items) : []

                items.forEach((item) => {
                    const actionPlans = item && item.actionplans ? item.actionplans : {}

                    Object.keys(actionPlans).forEach((key) => {
                        const actionPlan = actionPlans[key]
                        const ids = [key, actionPlan && actionPlan.id, actionPlan && actionPlan.id_checklist, actionPlan && actionPlan.checklist, actionPlan && actionPlan.checklist_id].filter((id) => id !== null && id !== undefined && id !== false && id !== '')

                        if (ids.some((id) => String(id) === actionPlanId)) {
                            actionPlan.status = payload.status
                        }
                    })
                })
            })
        })

        Vue.set(state, 'items', { ...state.items })
    },
    updateActionPlanPercentage(state, payload) {
        if (!payload || !payload.checkRef) {
            return
        }

        const auditId = payload.checkRef
        const auditSections = Object.values(state.items[auditId].sections)

        const auditItemsWithActionPlans = auditSections.flatMap((section) => Object.values(section.items).filter((item) => item.alerts !== false && item.alerts_triggers !== false && item.reference.type === 5))

        const actionPlans = Object.values(auditItemsWithActionPlans.map((auditItem) => auditItem.actionplans))
        const actionPlanItems = []

        actionPlans.forEach((actionPlans) => {
            Object.values(actionPlans).forEach((actionPlan) => {
                if (actionPlan.items) {
                    let items = Object.values(actionPlan.items)

                    items.forEach((item) => actionPlanItems.push(item))
                }
            })
        })

        const answeredActionPlanItemsQuantity = actionPlanItems.filter((item) => item.value !== null).length
        const itemsActives = actionPlanItems.filter((item) => Number(item.apply) === 1 && item.is_visible).length
        const percentage = parseInt((answeredActionPlanItemsQuantity / itemsActives) * 100)

        if (percentage) {
            state.items[auditId].data.actionplans_score = percentage
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setHasApiError: (state, hasApiError) => {
        state.hasApiError = hasApiError
    },
    setHasTemplatesApiError: (state, hasTemplatesApiError) => {
        state.hasTemplatesApiError = hasTemplatesApiError
    }
}

/**
 * Evalúa la visibilidad de un SUBITEM basado en sus condiciones
 * @param {Object} item - El item a evaluar
 * @param {Object} section - La sección que contiene el item
 * @returns {boolean} - true si el item debe ser visible, false si no
 */
function evaluateItemVisibility(item, section) {
    try {
        // Si no tiene condiciones, es visible por defecto
        if (typeof item.conditions === 'undefined' || item.conditions == null || !item.conditions) {
            return true
        }

        const subitemConditionsData = item.conditions
        const conditionsCombinator = typeof subitemConditionsData.operator !== 'undefined' ? subitemConditionsData.operator : 'AND'
        const conditions = subitemConditionsData.values

        let shouldDisplay = null

        // the condition id is the parent item id
        for (const parentId in conditions) {
            let show = false

            const condition = conditions[parentId]
            const conditionOperator = condition && condition.operator ? condition.operator : '='
            const conditionValue = condition && condition.value ? condition.value : false

            const parentItem = section.items[parentId] ? section.items[parentId] : null

            const parentItemValue = parentItem.value

            if ((parentItemValue !== null || (parentItem && parentItem.type && [FORMAT.CHECK, FORMAT.DOCUMENT].includes(parentItem.type))) && parentItem && parentItem.is_visible) {
                if (conditionOperator === 'true' && conditionValue === 'true') {
                    show = true
                } else {
                    const parentItemType = parentItem.type

                    switch (parseInt(parentItemType)) {
                        case FORMAT.CHECK:
                        case FORMAT.YESNO:
                        case FORMAT.YESNONC:
                        case FORMAT.TEXT:
                        case FORMAT.TEXTAREA:
                        case FORMAT.PHOTO:
                        case FORMAT.DATE:
                        case FORMAT.TIME:
                        case FORMAT.DATETIME:
                        case FORMAT.SIGNATURE:
                            show = compare(conditionValue, parentItemValue, conditionOperator)
                            break
                        case FORMAT.EMAIL:
                        case FORMAT.DOCUMENT:
                        case FORMAT.RESOURCE:
                        case FORMAT.ENLACE:
                            show = compare(parseInt(conditionValue) === 1 || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue === true || parentItemValue === 'true' || (parentItemValue !== null && conditionValue === '' && conditionOperator === '!empty'), conditionOperator)
                            break
                        case FORMAT.MULTIPLE:
                            const parentItemIsMultiselect = parentItem && parentItem.config && parentItem.config.hasOwnProperty('multiselect') && parentItem.config.multiselect === 1

                            if (!parentItemIsMultiselect) {
                                // SINGLE SELECT
                                show = compare(conditionValue || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue, conditionOperator)
                            } else {
                                // MULTIPLE SELECT
                                if (Array.isArray(parentItemValue) || (typeof parentItemValue === 'string' && parentItemValue.includes('|'))) {
                                    const parentItemValues = Array.isArray(parentItemValue) ? parentItemValue : parentItemValue.split('|')
                                    show = parentItemValues.some((value) => compare(conditionValue, value, conditionOperator))
                                } else {
                                    show = compare(conditionValue, parentItemValue, conditionOperator)
                                }
                            }
                            break
                        case FORMAT.NUMBER:
                        case FORMAT.TEMPERATURE:
                            const normalizedItemValue = parentItemValue.toString().replace(',', '.')

                            // importante: el segundo parámetro de compare (condition value) debe ser string
                            // ya que aquí se comparan todos los tipos de condiciones, incluidos "está entre" y "no está entre"
                            show = compare(parseFloat(normalizedItemValue), conditionValue, conditionOperator) || (conditionValue === '' && conditionOperator === '!empty')
                            break
                        default:
                            show = false
                            break
                    }
                }
            }

            if (shouldDisplay == null) shouldDisplay = show
            else {
                switch (conditionsCombinator) {
                    case 'AND':
                        shouldDisplay = shouldDisplay && show
                        break
                    case 'OR':
                        shouldDisplay = shouldDisplay || show
                        break
                }
            }
        }

        return shouldDisplay
    } catch (error) {
        console.error('Error evaluating item visibility:', error)
        return false
    }
}

function loadTemplatesOffline(context) {
    let templates = []
    let count = 0
    return db_templates
        .iterate(function (value, key, iterationNumber) {
            value = typeof value === 'string' ? JSON.parse(value) : value
            if (parseInt(value.type) == parseInt(context.state.type)) {
                templates.push(value)
                count++
            }
        })
        .then(function () {
            if (count > 0) {
                //sort by created date desc
                templates.sort((a, b) => {
                    return moment(a.created_date) - moment(b.created_date)
                })

                context.commit('setTemplates', { data: templates })
            }
        })
}

function loadItemsOffline(commit, dispatch) {
    let items = {}
    let count = 0
    return db_audits
        .iterate(function (value, key, iterationNumber) {
            items[value.id] = { ...value }
            count++
        })
        .then(async function () {
            if (count > 0) {
                dispatch('mergeOffline', { data: items }).then(async (response) => {
                    dispatch('format', { data: response.data })
                    await dispatch('calcScore')
                    await dispatch('actionPlanCalcScore')
                })
            }
        })
}

function loadDetailOffline(context, id) {
    const items = {}
    return db_audits
        .iterate(function (value, key, iterationNumber) {
            if (value.id == id) {
                items[value.id] = value
            }
        })
        .then(async function () {
            context.dispatch('format', { data: items })
            await context.dispatch('calcScore')
            await context.dispatch('actionPlanCalcScore')
        })
}

/**
 * Construye el objeto de la propiedad alerts del item basado en sus alert triggers
 * @param {Object} item - El item a procesar
 * @param {Object} FORMAT - Constantes de formato
 * @returns {Object|false} - Objeto de alertas o false si no hay alertas
 */
function buildItemAlerts(item, FORMAT) {
    if (!item.alerts_triggers) {
        return false
    }

    const alertsTriggers = item.alerts_triggers
    const itemValue = item.value

    let oAlert = {}
    let message = ''
    let preview = ''
    let issues = []
    let classes = []

    for (const i in alertsTriggers) {
        const alert = alertsTriggers[i]
        const operator = alert && alert.operator ? alert.operator : '='
        const alertValue = alert.value

        let show = false

        if ((itemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(item.type)) && item.is_visible) {
            if (operator === 'true' && alertValue === 'true') {
                show = true
            } else {
                switch (item.type) {
                    case FORMAT.CHECK:
                    case FORMAT.YESNO:
                        show = compare(alertValue, itemValue, operator)
                        break
                    case FORMAT.EMAIL:
                    case FORMAT.DOCUMENT:
                        show = compare(parseInt(alertValue) === 1 || (alertValue === '' && operator === '!empty'), itemValue === true || itemValue === 'true', operator)
                        break
                    case FORMAT.YESNONC:
                        show = compare(parseInt(alertValue) || (alertValue === '' && operator === '!empty'), itemValue, operator)
                        break
                    case FORMAT.MULTIPLE:
                        const itemIsMultiselect = item && item.config && item.config.hasOwnProperty('multiselect') && item.config.multiselect === 1

                        // MULTIPLE SELECT
                        if (itemIsMultiselect && (Array.isArray(itemValue) || (typeof itemValue === 'string' && itemValue.includes('|')))) {
                            const itemValues = Array.isArray(itemValue) ? itemValue : itemValue.split('|')

                            show = itemValues.some((value) => compare(alertValue || (alertValue === '' && operator === '!empty'), value, operator))
                        } else {
                            // SINGLE SELECT
                            show = compare(alertValue || (alertValue === '' && operator === '!empty'), itemValue, operator)
                        }
                        break
                    case FORMAT.TEXT:
                    case FORMAT.TEXTAREA:
                    case FORMAT.PHOTO:
                    case FORMAT.DATE:
                    case FORMAT.TIME:
                    case FORMAT.DATETIME:
                    case FORMAT.SIGNATURE:
                        show = compare(alertValue, itemValue, operator)
                        break
                    case FORMAT.NUMBER:
                        const aux = itemValue.toString().replace(',', '.')
                        show = compare(parseFloat(aux), alertValue, operator) || (itemValue !== '-' && operator === '!empty')
                        break
                    case FORMAT.TEMPERATURE:
                        const auxTemp = itemValue.toString().replace(',', '.')
                        show = compare(parseFloat(auxTemp), alertValue, operator) || (itemValue !== '-' && operator === '!empty')
                        break
                    default:
                        break
                }
            }
        }

        if (show && alert.msg) {
            if (message === '') message = alert.msg
            else message = message + '<br/>' + alert.msg

            if (preview === '') preview = alert.msg
            else preview = preview + ', ' + alert.msg
        }

        if (show && alert.tpl) {
            issues.push(alert.tpl)
        }
        if (show && alert.class) {
            if (alert.class === 'alert') {
                alert.class = 'warning'
            }
            classes.push(alert.class)
        }
    }

    oAlert = {
        preview: preview,
        message: message,
        issues: issues,
        class: classes
    }

    if (oAlert.message.length > 0 || oAlert.issues.length > 0) {
        return oAlert
    }

    return false
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
